import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Button, Col, Row, Spinner, Form, Modal, Nav } from 'react-bootstrap';
import { faMapMarkerAlt, faClipboardList, faTimes, faExclamationCircle, faPen, faCamera, faImage, faChevronUp, faChevronDown,faPaperPlane, faClock, faCheck, faSignature, faStar, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { toast } from "react-toastify";
import axios from "axios";
import classNames from 'classnames';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DatePicker from "react-datepicker";
import TimeInput from "../proposals/timeInput";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Offcanvas } from 'react-bootstrap';
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import SignatureCanvas from "react-signature-canvas";
import SubtleBadge from "components/common/subtleBadge";
import SignatureModal from "components/profile/signatureModal";
import { getDomainConfig } from "../../config/domainConfig";
import FalconCloseButton from "components/common/falconCloseButton";
import imageCompression from 'browser-image-compression';
import { USER_ROLES } from "config/roles.config";
import { getFrequencyPillColor, dispatchBatchNotification, checkEmployeeAvailability, clearEmployeeAvailabilityCache, SchedulingIssuesModal, fetchRecommendedEmployees, partitionRecommendedEmployees, sortEmployeesAscending } from "../../assets/js/common";
import useSocket from '../../hooks/useSocket';
import { getOccurrenceCount } from '../../utils/recurrenceHelpers';

const MAX_FILE_SIZE_MB = parseFloat(process.env.REACT_APP_MAX_FILE_SIZE_MB);
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_FILE_TYPES = (process.env.REACT_APP_ALLOWED_FILE_TYPES).split(',').map(ext => ext.trim().toLowerCase());
const MAX_PAYLOAD_SIZE_MB = parseFloat(process.env.REACT_APP_MAX_TOTAL_PAYLOAD_MB);
const MAX_PAYLOAD_SIZE = MAX_PAYLOAD_SIZE_MB * 1024 * 1024;

const COMPRESSION_OPTIONS = {
    maxSizeMB: MAX_FILE_SIZE_MB,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: 'image/jpeg',
};

const COMPLAINT_REASONS = [
    { key: "serviceNotCompleted", label: "Service not completed" },
    { key: "poorQualityOfWork", label: "Poor quality of work" },
    { key: "employeeArrivedLate", label: "Employee arrived late" },
    { key: "employeeDidNotArrive", label: "Employee did not arrive" },
    { key: "incorrectServiceProvided", label: "Incorrect service provided" },
    { key: "unprofessionalBehaviour", label: "Unprofessional behaviour" },
    { key: "propertyDamage", label: "Property damage" },
    { key: "billingIssue", label: "Billing issue" },
    { key: "otherWithComments", label: "Other" }
];

const FixedDatePicker = ({ field, error, maxDate, ...props }) => (
    <div className="date-picker-wrapper w-100 position-relative">
        <DatePicker
            {...field}
            selected={field.value}
            onChange={(date) => field.onChange(date)}
            className={`form-control form-control-sm ${error ? "is-invalid" : ""}`}
            dateFormat="dd-MM-yyyy"
            placeholderText="dd-mm-yyyy"
            maxDate={maxDate}
            wrapperClassName="w-100"
            portalId="datepicker-portal-root"
            popperPlacement="bottom-start"
            {...props}
        />

        {error && (
            <div className="invalid-feedback d-block">
                {error.message}
            </div>
        )}
    </div>
);

const FixedTimePicker = ({ field, error, ...props }) => (
    <div className="date-picker-wrapper w-100 position-relative">
        <DatePicker
            {...field}
            selected={field.value}
            onChange={(time) => field.onChange(time)}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="HH:mm"
            timeFormat="HH:mm"
            placeholderText="HH:mm"
            className={`form-control form-control-sm ${error ? "is-invalid" : ""}`}
            wrapperClassName="w-100"
            portalId="datepicker-portal-root"
            popperPlacement="bottom-start"
            {...props}
        />

        {error && (
            <div className="invalid-feedback d-block">
                {error.message}
            </div>
        )}
    </div>
);

const TaskDrawer = ({
    open,
    onClose,
    selectedTask,
    loading = false,
    t,
    renderStatusBadge,
    userRole,
    fetchEmployeeTasks,
    fetchCalendarData,
    calendarStart,
    calendarEnd,
    context,
    allEmployees = [],
    relationMap,
    onRemoveCleaner,
    fetchOrderDetails,
    onTaskUpdated,
    onStartJob,
    onStepCompletionsChange,
    conflictMap = {},
    initialSection = "overview", 
}) => {
    const handleEmployeeSelect = (emp) => {
        setSelectedEmployee(emp);
        setIsOpen(false); // close dropdown after select
    };

    // Check if task is overdue (has no ActualStartTime but is in the past)
    const isOverdueTask = selectedTask && !selectedTask.ActualStartTime && moment(selectedTask.Date || selectedTask.WeekStartDate).isBefore(moment(), 'day');
    const schema = yup.object({
        editStartDate: yup
            .date()
            .required(t("startDateRequired"))
            .typeError(t("invalidStartDate"))
            .test(
                "startDate-not-future",
                t("startDateCannotBeFuture"),
                function (startDate) {
                    if (startDate) {
                        return moment(startDate).isSameOrBefore(moment(), "day");
                    }
                    return true;
                }
            )
            .test(
                "startDate-not-today-for-overdue",
                t("startDateCannotBeTodayForOverdue"),
                function (startDate) {
                    if (startDate && isOverdueTask) {
                        return moment(startDate).isBefore(moment(), "day");
                    }
                    return true;
                }
            ),

        editEndDate: yup
            .date()
            .required(t("endDateRequired"))
            .typeError(t("invalidEndDate"))
            .test(
                "endDate-same-or-after-startDate",
                t("endDateAfterStartDate"),
                function (endDate) {
                    const { editStartDate } = this.parent;
                    if (endDate && editStartDate) {
                        //  allow same date
                        return moment(endDate).isSameOrAfter(moment(editStartDate), "day");
                    }
                    return true;
                }
            )
            .test(
                "endDate-not-future",
                t("endDateCannotBeFuture"),
                function (endDate) {
                    if (endDate) {
                        return moment(endDate).isSameOrBefore(moment(), "day");
                    }
                    return true;
                }
            )
            .test(
                "endDate-not-today-for-overdue",
                t("endDateCannotBeTodayForOverdue"),
                function (endDate) {
                    if (endDate && isOverdueTask) {
                        return moment(endDate).isBefore(moment(), "day");
                    }
                    return true;
                }
            ),

        editStartTime: yup
            .date()
            .required(t("startTimeRequired"))
            .typeError(t("invalidStartTime")),

        editEndTime: yup
            .date()
            .nullable()
            .test(
                "editEndTime-required",
                t("endTimeRequired"),
                function (value) {
                    // Only require editEndTime when editing date/time for overdue tasks
                    const { editStartTime } = this.parent;
                    if (isOverdueTask && isEditingDateTime && editStartTime) {
                        return !!value;
                    }
                    return true;
                }
            )
            .typeError(t("invalidEndTime"))
            .test(
                "endTime-same-or-after-startTime",
                t("endTimeAfterStartTime"),
                function (endTime) {
                    const { editStartTime } = this.parent;
                    if (endTime && editStartTime) {
                        //  allow same time
                        return moment(endTime).isSameOrAfter(moment(editStartTime));
                    }
                    return true;
                }
            ),
        actualEndTime: yup
            .date()
            .nullable()
            .test(
                "actualEndTime-required",
                t("endTimeRequired"),
                function (value) {
                    // Only require actualEndTime when ending a job (not for overdue tasks in date/time edit)
                    const { actualStartTime } = this.parent;
                    if (isEditingJob && actualStartTime && !isOverdueTask) {
                        return !!value;
                    }
                    return true;
                }
            )
            .typeError(t("invalidEndTime"))
            .test(
                "actualEndTime-after-actualStartTime",
                t("endTimeAfterStartTime"),
                function (endTime) {
                    const { actualStartTime } = this.parent;
                    if (endTime && actualStartTime) {
                        return moment(endTime).isSameOrAfter(moment(actualStartTime));
                    }
                    return true;
                }
            ),
        plannedEmployeeId: yup
            .string()
            .required(t("employeeRequired")),

        plannedStartDate: yup
            .date()
            .required(t("startDateRequired"))
            .typeError(t("invalidStartDate")),

        plannedStartTime: yup
            .date()
            .required(t("startTimeRequired"))
            .typeError(t("invalidStartTime")),

        plannedEndTime: yup
            .date()
            .required(t("endTimeRequired"))
            .test(
                "plannedEndTime-required",
                t("endTimeRequired"),
                function (value) {
                    const { plannedStartTime } = this.parent;
                    if (isOverdueTask && isEditingDateTime && plannedStartTime) {
                        return !!value;
                    }
                    return true;
                }
            )
            .typeError(t("invalidEndTime"))
            .test(
                "plannedEndTime-after-startTime",
                t("endTimeAfterStartTime"),
                function (endTime) {
                    const { plannedStartTime } = this.parent;
                    if (endTime && plannedStartTime) {
                        return moment(endTime).isSameOrAfter(moment(plannedStartTime));
                    }
                    return true;
                }
            )
    });

    const [, forceUpdate] = useState(0);
    const topSubtasks = [
        { SubtaskId: "static-top-1", Subtask: t("subtasks.arrivedAtLocation") },
        { SubtaskId: "static-top-2", Subtask: t("subtasks.workStarted") }
    ];
    const [noteValue, setNoteValue] = useState("");
    const [isEditingNote, setIsEditingNote] = useState(false);
    const [editingField, setEditingField] = useState(null); 
    const isEditingDateTime = editingField !== null;
    const [editStartDate, setEditStartDate] = useState("");
    const [editEndDate, setEditEndDate] = useState("");
    const [editStartTime, setEditStartTime] = useState("");
    const [editEndTime, setEditEndTime] = useState("");
    const [isEditingEndJob, setIsEditingEndJob] = useState(false);
    const relation = relationMap?.[selectedTask?.RelationId] ?? selectedTask?.Relation ?? {};
    const [isEditingJob, setIsEditingJob] = useState(false);
    const [totalHours, setTotalHours] = useState(0);
    const [dailyCumulativeHours, setDailyCumulativeHours] = useState(0);
    const [apiDailyHours, setApiDailyHours] = useState(0); // total hours from API
    const isEmployeeView = sessionStorage.getItem("isEmployeeView") === "true";
    const [isSaving, setIsSaving] = useState(false);
    const [breakMinutes, setBreakMinutes] = useState(0);
    const signaturePadRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [recommendedEmployeeGroups, setRecommendedEmployeeGroups] = useState(null);
    const recommendationRequestRef = useRef(0);
    const recommendationDebounceRef = useRef(null);
    const [showEmailConfigModal, setShowEmailConfigModal] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [tempClientSignature, setTempClientSignature] = useState(null);
    const [stepCompletions, setStepCompletions] = useState({});
    const [stepSaving, setStepSaving] = useState({});
    const stepCompletionsCache = useRef({});
    const [stepsLoading, setStepsLoading] = useState(false);
    const [taskPhotos, setTaskPhotos] = useState([]);
    const [isMobileDevice, setIsMobileDevice] = useState(false);
    const [completionImages, setCompletionImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const imageCache = useRef({});
    const reviewCache = useRef({});
    const [previewIndex, setPreviewIndex] = useState(null);
    const [galleryPreview, setGalleryPreview] = useState(null); 
    const openGalleryPreview = (images, index) => {
        setGalleryPreview({ images, index });
    };
    const [proposalFlags, setProposalFlags] = useState({ clientSignatureRequired: false, pauseRequired: false, });
    const [isWorkflowOpen, setIsWorkflowOpen] = useState(true);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showCombinedIssuesModal, setShowCombinedIssuesModal] = useState(false);
    const [pendingCombinedIssues, setPendingCombinedIssues] = useState(null);
    const [taskNotes, setTaskNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNoteText, setNewNoteText] = useState("");
    const [isSubmittingNote, setIsSubmittingNote] = useState(false);
    const [selectedNoteImages, setSelectedNoteImages] = useState([]);
    const [showChatAttachMenu, setShowChatAttachMenu] = useState(false);
    const [activeSection, setActiveSection] = useState("overview");
    const [isWritingReview, setIsWritingReview] = useState(false);

    useEffect(() => {
        setActiveSection(initialSection);
    }, [open, selectedTask?.OrderDetailId, initialSection]);

    const [reviewRating, setReviewRating] = useState(0);
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);
    const [hoveredStar, setHoveredStar] = useState(0);
    const [reviewComment, setReviewComment] = useState("");
    const [existingReview, setExistingReview] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [unreadNotesCount, setUnreadNotesCount] = useState(0);
    const [feedbackImages, setFeedbackImages] = useState([]);
    const [showFeedbackAttachMenu, setShowFeedbackAttachMenu] = useState(false);

        const feedbackPhotoUrls = useMemo(
        () => feedbackImages.map((photo) => URL.createObjectURL(photo)),
        [feedbackImages]
    );
    
    useEffect(() => {
        return () => {
            feedbackPhotoUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [feedbackPhotoUrls]);

    // --- Complaint State ---
    const complaintCache = useRef({});
    const [showComplaintModal, setShowComplaintModal] = useState(false);
    const [selectedComplaintReason, setSelectedComplaintReason] = useState("");
    const [complaintReasonError, setComplaintReasonError] = useState(false);
    const [complaintComment, setComplaintComment] = useState("");
    const [complaintImages, setComplaintImages] = useState([]);
    const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);
    const [existingComplaint, setExistingComplaint] = useState(null);
    const [complaintLoading, setComplaintLoading] = useState(false);

    const complaintPhotoUrls = useMemo(
        () => complaintImages.map((photo) => URL.createObjectURL(photo)),
        [complaintImages]
    );

    useEffect(() => {
        return () => {
            complaintPhotoUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [complaintPhotoUrls]);

    const noteImageUrls = useMemo(
        () => selectedNoteImages.map((photo) => URL.createObjectURL(photo)),
        [selectedNoteImages]
    );

    useEffect(() => {
        return () => {
            noteImageUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [noteImageUrls]);

    const taskPhotoUrls = useMemo(
        () => taskPhotos.map((photo) => URL.createObjectURL(photo)),
        [taskPhotos]
    );
    useEffect(() => {
        return () => {
            taskPhotoUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [taskPhotoUrls]);

    const getColorFromName = (name) => {
        const colors = [
            "bg-primary-subtle text-primary-emphasis",
            "bg-success-subtle text-success-emphasis",
            "bg-warning-subtle text-warning-emphasis",
            "bg-danger-subtle text-danger-emphasis",
            "bg-info-subtle text-info-emphasis",
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const getDiscussionAvatarColorClass = (name) => {
        const colors = [
            "bg-primary-subtle text-primary-emphasis",
            "bg-success-subtle text-success-emphasis",
            "bg-warning-subtle text-warning-emphasis",
            "bg-danger-subtle text-danger-emphasis",
            "bg-info-subtle text-info-emphasis",
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const normalizeRole = (role) => {
        if (!role) return "";
        return role.toString().trim().toLowerCase().replace(/\s+/g, "");
    };


    const getRoleBadgeStyle = (role) => {
        const map = {
            client: "bg-primary-subtle text-primary",
            employee: "bg-success-subtle text-success",
            appowner: "bg-warning-subtle text-warning",
            admin: "bg-danger-subtle text-danger",
        };
        return map[normalizeRole(role)] || "bg-secondary-subtle text-secondary";
    };

    const getRatingLabel = (rating) => {
        const labels = {
            1: t("ratingPoor"),
            2: t("ratingFair"),
            3: t("ratingGoodWork"),
            4: t("ratingGreat"),
            5: t("ratingExcellent"),
        };
        return labels[rating] || "";
    };

    const handleReviewSubmit = async () => {
        if (reviewRating === 0) {
            toast.error(t("pleaseSelectRating"), { position: "top-right" });
            return;
        }

        const totalFeedbackPayloadSize = feedbackImages.reduce((sum, f) => sum + f.size, 0);
            if (totalFeedbackPayloadSize > MAX_PAYLOAD_SIZE) {
                toast.error(
                    t('totalPayloadExceeded', { maxSize: MAX_PAYLOAD_SIZE_MB }),
                    { position: 'top-right' }
                );
                return;
            }
        
        setIsSubmittingReview(true);
        try {
            const customerId = localStorage.getItem("customerId");

            const images = await Promise.all(
                feedbackImages.map((photo) => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            resolve({
                                base64: reader.result.split(",")[1],
                                extension: photo.type.split("/")[1],
                            });
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(photo);
                    });
                })
            );

            const taskDateStr = moment(selectedTask?.WeekStartDate || selectedTask?.Date).format("DD-MM-YYYY");
            const taskDateISO = moment(selectedTask?.WeekStartDate || selectedTask?.Date).format("YYYY-MM-DD");
            const taskStartTimeStr = selectedTask?.StartTime ? moment.utc(selectedTask.StartTime).local().format("HH:mm") : "";
            const taskEndTimeStr = selectedTask?.EndTime ? moment.utc(selectedTask.EndTime).local().format("HH:mm") : "";

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/orderDetailReviewRoute/save`,
                {
                    orderDetailId: selectedTask.OrderDetailId,
                    customerId,
                    rating: reviewRating,
                    comment: reviewComment.trim(),
                    images,
                    relationId: selectedTask?.RelationId || selectedTask?.Relation?.RelationId || relation?.RelationId || null,
                    taskName: selectedTask?.Task?.TaskName?.trim() || selectedTask?.TaskName || "",   
                    taskDate: taskDateStr,   
                    taskDateISO,                                                           
                    taskTime: taskStartTimeStr && taskEndTimeStr ? `${taskStartTimeStr} - ${taskEndTimeStr}` : "", 
                    culture: localStorage.getItem("culture") || "nl",
                    clientName: selectedTask?.Relation?.Name || relation?.Name || "", 
                },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            toast.success(t("reviewSubmittedSuccessfully"), { position: "top-right" });

            setExistingReview(res.data.review);  
            reviewCache.current[selectedTask.OrderDetailId] = res.data.review;
            setIsWritingReview(false);             
            setReviewRating(0);                    
            setReviewComment("");                  
            setFeedbackImages([]);                 
        } catch (err) {
            console.error("Failed to submit review:", err);
            toast.error(t("reviewSubmitError"), { position: "top-right" });
        } finally {
            setIsSubmittingReview(false);
        }
    };

    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(schema)
    });

    const {
        control,
        formState: { errors },
        reset,
        getValues,
        trigger,
        setValue,
        watch
    } = methods;

    // useWatch for actualEndTime so we can compute valid break options reactively
    const actualEndTimeValue = useWatch({ control, name: "actualEndTime" });

    // Watch for date changes to update time fields
    const editStartDateValue = useWatch({ control, name: "editStartDate" });
    const editEndDateValue = useWatch({ control, name: "editEndDate" });

    // Add this function to check order status and update if all jobs are completed
    const checkAndUpdateOrderStatus = async (orderId) => {
        try {
            // First, get the job status summary
            const summaryResponse = await axios.get(
                `${process.env.REACT_APP_API_URL}/orderRoute/${orderId}/jobStatusSummary`,
                {
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
            );

            if (summaryResponse.status === 200) {
                const summary = summaryResponse.data;

                // Check if total count equals completed status count
                if (summary.totalJobs === summary.completed && summary.totalJobs > 0) {
                    // Update order status to Completed
                    await axios.patch(
                        `${process.env.REACT_APP_API_URL}/orderRoute/updateOrderStatus/${orderId}`,
                        { status: "Completed" },
                        {
                            headers: {
                                Authorization: process.env.REACT_APP_BasicAuthSecret
                            }
                        }
                    );
                }
            }
        } catch (error) {
            console.error("Error checking/updating order status:", error);
            // Don't show error to user as this is a background process
        }
    };

    const normalizePlannedDateTime = (task) => {
        if (!task?.StartTime || !task?.EndTime) return {};

        const dateStr = moment(task.WeekStartDate).format("YYYY-MM-DD");

        const startTimeOnly = moment.utc(task.StartTime).format("HH:mm:ss");
        const endTimeOnly = moment.utc(task.EndTime).format("HH:mm:ss");

        const plannedStart = moment.utc(`${dateStr}T${startTimeOnly}`).local();
        const plannedEnd = moment.utc(`${dateStr}T${endTimeOnly}`).local();

        return {
            plannedStartDate: moment(dateStr).toDate(),
            plannedStartTime: plannedStart.toDate(),
            plannedEndTime: plannedEnd.toDate()
        };
    };

    useEffect(() => {
        if (isEditingJob) {
            setValue("status", "2");
        }
    }, [isEditingJob, setValue]);

    useEffect(() => {
        if (!open) {
            setIsEditingJob(false);
            setTempClientSignature(null);
            reset({
                editStartTime: null,
                editEndTime: null,
                status: "",
            });
        }
    }, [open, reset]);

    useEffect(() => {
        const checkMobileDevice = () => {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const mobile = window.innerWidth <= 1024;
            setIsMobileDevice(mobile);
        };

        checkMobileDevice();
        window.addEventListener("resize", checkMobileDevice);
        return () => window.removeEventListener("resize", checkMobileDevice);
    }, []);

    useEffect(() => {
        if (isEditingJob && !isOverdueTask) {
            const taskDateStr = moment(
                selectedTask?.WeekStartDate || selectedTask?.Date || new Date()
            ).format("YYYY-MM-DD");

            // Planned start time pre-filled
            const plannedStartTimeOnly = selectedTask?.StartTime
                ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.StartTime).format("HH:mm:ss")}`).local()
                : null;

            // Planned end time pre-filled
            const plannedEndTimeOnly = selectedTask?.EndTime
                ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.EndTime).format("HH:mm:ss")}`).local()
                : null;

            if (plannedStartTimeOnly) setValue("actualStartTime", plannedStartTimeOnly.toDate());
            if (plannedEndTimeOnly) setValue("actualEndTime", plannedEndTimeOnly.toDate());
        }

        // Overdue tasks 
        if (isEditingJob && isOverdueTask) {
            const taskDateStr = moment(
                selectedTask.Date || selectedTask.WeekStartDate || new Date()
            ).format("YYYY-MM-DD");

            const startTimeOnly = selectedTask?.Start && moment(selectedTask.Start, "HH:mm", true).isValid()
                ? selectedTask.Start
                : selectedTask?.StartTime
                    ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.StartTime).format("HH:mm:ss")}`).local().format("HH:mm")
                    : "09:00";

            const endTimeOnly = selectedTask?.End && moment(selectedTask.End, "HH:mm", true).isValid()
                ? selectedTask.End
                : selectedTask?.EndTime
                    ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.EndTime).format("HH:mm:ss")}`).local().format("HH:mm")
                    : "12:00";

            const startDateValue = moment(`${taskDateStr}T${startTimeOnly}`).toDate();
            const endDateValue = moment(`${taskDateStr}T${endTimeOnly}`).toDate();

            setValue("editStartDate", startDateValue);
            setValue("editEndDate", endDateValue);
            setValue("editStartTime", startDateValue);
            setValue("editEndTime", endDateValue);
        }
    }, [isEditingJob, selectedTask, setValue, isOverdueTask]);

    // Update start time when start date changes for overdue tasks
    useEffect(() => {
        if (isOverdueTask && isEditingJob && editStartDateValue) {
            const currentStartTime = watch("editStartTime");
            if (currentStartTime) {
                const newStartTime = moment(editStartDateValue)
                    .set({
                        hour: moment(currentStartTime).hour(),
                        minute: moment(currentStartTime).minute()
                    })
                    .toDate();
                setValue("editStartTime", newStartTime);
            }
        }
    }, [editStartDateValue, isOverdueTask, isEditingJob, setValue, watch]);

    // Update end time when end date changes for overdue tasks
    useEffect(() => {
        if (isOverdueTask && isEditingJob && editEndDateValue) {
            const currentEndTime = watch("editEndTime");
            if (currentEndTime) {
                const newEndTime = moment(editEndDateValue)
                    .set({
                        hour: moment(currentEndTime).hour(),
                        minute: moment(currentEndTime).minute()
                    })
                    .toDate();
                setValue("editEndTime", newEndTime);
            }
        }
    }, [editEndDateValue, isOverdueTask, isEditingJob, setValue, watch]);

    useEffect(() => {
        const start = watch("editStartTime");
        const end = watch("editEndTime");
        if (start && end) {
            const duration = moment(end).diff(moment(start), "hours", true);
            setTotalHours(duration > 0 ? duration : 0);
        }
    }, [watch("editStartTime"), watch("editEndTime")]);

    useEffect(() => {
        if (userRole === USER_ROLES.EMPLOYEE || isEmployeeView) {
            const start = watch("editStartTime");
            const end = watch("editEndTime");

            if (start && end && moment(end).isAfter(moment(start))) {
                const currentJobHours = moment(end).diff(moment(start), "minutes") / 60;
                setDailyCumulativeHours(apiDailyHours + currentJobHours);
            } else {
                setDailyCumulativeHours(apiDailyHours);
            }
        }
    }, [watch("editStartTime"), watch("editEndTime"), apiDailyHours, userRole]);

    useEffect(() => {
        if (!isEditingDateTime || userRole === USER_ROLES.CLIENT) return;
        if (!selectedTask?.CleanerId) return;
        if (!allEmployees?.length) return;
        const emp = allEmployees.find(
            e => String(e.UserId) === String(selectedTask.CleanerId)
        );

        if (emp) {
            setSelectedEmployee(emp);
            setValue("employee", emp.UserId);
        }
    }, [isEditingDateTime, selectedTask, allEmployees, setValue]);

    useEffect(() => {
        if (selectedTask) {
            const actualStartDate = selectedTask?.ActualStartDate;
            const actualEndDate = selectedTask?.ActualEndDate;

            const actualStartTime = selectedTask?.ActualStartTime
                ? moment.utc(selectedTask.ActualStartTime).format("HH:mm:ss")
                : null;

            const actualEndTime = selectedTask?.ActualEndTime
                ? moment.utc(selectedTask.ActualEndTime).format("HH:mm:ss")
                : null;

            const actualStart = (actualStartDate && actualStartTime)
                ? moment.utc(`${actualStartDate}T${actualStartTime}`).local()
                : null;

            const actualEnd = (actualEndDate && actualEndTime)
                ? moment.utc(`${actualEndDate}T${actualEndTime}`).local()
                : null;

            if (actualStart) setValue("editStartTime", actualStart.toDate());
            if (actualEnd) setValue("editEndTime", actualEnd.toDate());
        }
    }, [selectedTask, setValue]);

    useEffect(() => {
        if (selectedTask) {
            const taskDateStr = moment(
                selectedTask.Date || selectedTask.WeekStartDate || new Date()
            ).format("YYYY-MM-DD");

            const startTimeOnly =
                selectedTask?.Start && moment(selectedTask.Start, "HH:mm", true).isValid()
                    ? selectedTask.Start
                    : selectedTask?.StartTime
                        ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.StartTime).format("HH:mm:ss")}`).local().format("HH:mm")
                        : "09:00";

            const endTimeOnly =
                selectedTask?.End && moment(selectedTask.End, "HH:mm", true).isValid()
                    ? selectedTask.End
                    : selectedTask?.EndTime
                        ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.EndTime).format("HH:mm:ss")}`).local().format("HH:mm")
                        : "12:00";

            const startDateValue = moment(`${taskDateStr}T${startTimeOnly}`).toDate();
            const endDateValue = moment(`${taskDateStr}T${endTimeOnly}`).toDate();

            setEditStartDate(taskDateStr);
            setEditEndDate(taskDateStr);
            setEditStartTime(startTimeOnly);
            setEditEndTime(endTimeOnly);

            setValue("editStartDate", startDateValue);
            setValue("editEndDate", endDateValue);
            setValue("editStartTime", startDateValue);
            setValue("editEndTime", endDateValue);
        }
    }, [selectedTask, setValue]);

    useEffect(() => {
        if (!open && selectedTask) {
            setIsEditingNote(false);
            setEditingField(null);
            setNoteValue(selectedTask?.Notes || "");

            const taskDateStr = moment(
                selectedTask.Date || selectedTask.WeekStartDate || new Date()
            ).format("YYYY-MM-DD");

            const startTimeOnly =
                selectedTask?.Start && moment(selectedTask.Start, "HH:mm", true).isValid()
                    ? selectedTask.Start
                    : selectedTask?.StartTime
                        ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.StartTime).format("HH:mm:ss")}`).local().format("HH:mm")
                        : "09:00";

            const endTimeOnly =
                selectedTask?.End && moment(selectedTask.End, "HH:mm", true).isValid()
                    ? selectedTask.End
                    : selectedTask?.EndTime
                        ? moment.utc(`${taskDateStr}T${moment.utc(selectedTask.EndTime).format("HH:mm:ss")}`).local().format("HH:mm")
                        : "12:00";

            const startDateValue = moment(`${taskDateStr}T${startTimeOnly}`).toDate();
            const endDateValue = moment(`${taskDateStr}T${endTimeOnly}`).toDate();

            setEditStartDate(taskDateStr);
            setEditEndDate(taskDateStr);
            setEditStartTime(startTimeOnly);
            setEditEndTime(endTimeOnly);

            reset({
                editStartDate: startDateValue,
                editEndDate: endDateValue,
                editStartTime: startDateValue,
                editEndTime: endDateValue
            });
        }
    }, [open, selectedTask, reset]);

    const fetchStepCompletions = async (orderDetailId) => {
        if (!orderDetailId) return;

        // Storing in cache to avoid unnecessary API calls if the data is already fetched
        if (stepCompletionsCache.current[orderDetailId]) {
            setStepCompletions(stepCompletionsCache.current[orderDetailId]);
            return;
        }

        setStepsLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/orderDetailSubtaskCompletionRoute/${orderDetailId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            const map = {};
            (res.data || []).forEach((c) => {
                const key = (c.ProposalSubtaskId || "").trim(); 
                map[key] = {
                    CompletedAt: c.CompletedAt,
                    EmployeeId: c.EmployeeId,
                };
            });
            stepCompletionsCache.current[orderDetailId] = map;
            setStepCompletions(map);
        } catch (err) {
            console.error("Failed to fetch step completions:", err);
        } finally {
            setStepsLoading(false);
        }
    };

    const fetchTaskNotes = async (orderDetailId) => {
        if (!orderDetailId) return;
        setNotesLoading(true);
        try {
            const customerId = localStorage.getItem("customerId");
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/taskDiscussionRoute/${orderDetailId}`,
                { params: { customerId }, headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setTaskNotes(res.data || []);
        } catch (err) {
            console.error("Failed to fetch task notes:", err);
        } finally {
            setNotesLoading(false);
        }
    };

    const fetchUnreadCount = async (orderDetailId) => {
        try {
            const userId = localStorage.getItem("UserId");
            const customerId = localStorage.getItem("customerId");
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/taskDiscussionRoute/${orderDetailId}/unreadCount`,
                { params: { userId, customerId }, headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setUnreadNotesCount(res.data.unreadCount || 0);
        } catch (err) {
            console.error("Failed to fetch unread count:", err);
        }
    };

    const markDiscussionAsSeen = async () => {
        try {
            const orderDetailId = selectedTask?.OrderDetailId;
            const userId = localStorage.getItem("UserId");
            const customerId = localStorage.getItem("customerId");
            if (!orderDetailId || !userId) return;
            await axios.post(
                `${process.env.REACT_APP_API_URL}/taskDiscussionRoute/markAsRead`,
                { orderDetailId, userId, customerId },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            setUnreadNotesCount(0);
        } catch (err) {
            console.error("Failed to mark discussion as read:", err);
        }
    };

    useEffect(() => {
        if (!open || !selectedTask?.OrderDetailId) return;
        fetchStepCompletions(selectedTask.OrderDetailId);
    }, [open, selectedTask?.OrderDetailId]);

    useEffect(() => {
        if (!open || !selectedTask?.OrderDetailId) return;
        fetchTaskNotes(selectedTask.OrderDetailId);
        fetchUnreadCount(selectedTask.OrderDetailId);
    }, [open, selectedTask?.OrderDetailId]);

    useEffect(() => {
        if (activeSection !== "review" || !selectedTask?.OrderDetailId) return;

        const orderDetailId = selectedTask.OrderDetailId;

        if (reviewCache.current[orderDetailId] !== undefined) {
            setExistingReview(reviewCache.current[orderDetailId]);
            return;
        }

        const fetchReview = async () => {
            setReviewLoading(true);
            try {
                const customerId = localStorage.getItem("customerId");
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/orderDetailReviewRoute/${orderDetailId}`,
                    {
                        params: { customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                    }
                );
                const reviewData = res.data || null;
                reviewCache.current[orderDetailId] = reviewData; 
                setExistingReview(reviewData);
            } catch (err) {
                console.error("Failed to fetch review:", err);
            } finally {
                setReviewLoading(false);
            }
        };

        fetchReview();
    }, [activeSection, selectedTask?.OrderDetailId]);

           useEffect(() => {
        if ((activeSection !== "review" && activeSection !== "complaint") || !selectedTask?.OrderDetailId) return;

        const orderDetailId = selectedTask.OrderDetailId;

        if (complaintCache.current[orderDetailId] !== undefined) {
            setExistingComplaint(complaintCache.current[orderDetailId]);
            return;
        }

        const fetchComplaint = async () => {
            setComplaintLoading(true);
            try {
                const customerId = localStorage.getItem("customerId");
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/orderDetailComplaintRoute/${orderDetailId}`,
                    {
                        params: { customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
                    }
                );
                // getByOrderDetailId returns an array; surface the latest complaint
                const complaintData = Array.isArray(res.data) ? (res.data[0] || null) : (res.data || null);
                complaintCache.current[orderDetailId] = complaintData;
                setExistingComplaint(complaintData);
            } catch (err) {
                console.error("Failed to fetch complaint:", err);
                setExistingComplaint(null);
            } finally {
                setComplaintLoading(false);
            }
        };

        fetchComplaint();
    }, [activeSection, selectedTask?.OrderDetailId]);

    useEffect(() => {
        setNewNoteText("");
        setSelectedNoteImages([]);
        setShowChatAttachMenu(false);
    }, [open, selectedTask?.OrderDetailId]);

    useSocket('taskDiscussion:created', (note) => {
        if (note?.OrderDetailId !== selectedTask?.OrderDetailId) return;
        setTaskNotes(prev => {
            if (prev.some(n => n.TaskDiscussionId === note.TaskDiscussionId)) return prev;
            return [note, ...prev];
        });

        const userId = localStorage.getItem("UserId");
        if (note.AuthorId !== userId) {
            if (activeSection === "discussion") {
                markDiscussionAsSeen();
            } else {
                setUnreadNotesCount(prev => prev + 1);
            }
        }
    });

    useEffect(() => {
        if (!open || !selectedTask?.OrderDetailId) return;

        const orderDetailId = selectedTask.OrderDetailId;

        if (imageCache.current[orderDetailId]) {
            setCompletionImages(imageCache.current[orderDetailId]);
            return;
        }

        const fetchCompletionImages = async () => {
            setImagesLoading(true);
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/orderDetailCompletionImageRoute/${selectedTask.OrderDetailId}`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                const data = res.data || [];
                imageCache.current[orderDetailId] = data;
                setCompletionImages(data);
            } catch (err) {
                console.error("Failed to fetch completion images:", err);
            }
            finally {
                setImagesLoading(false);
            }
        };

        fetchCompletionImages();
    }, [open, selectedTask?.OrderDetailId]);

    useEffect(() => {
        if (!open || !selectedTask?.ProposalId) return;

        const fetchProposalFlags = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalById/${selectedTask.ProposalId}`,
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                setProposalFlags({
                    clientSignatureRequired: Boolean(res.data?.ClientSignatureRequired),
                    pauseRequired: Boolean(res.data?.PauseRequired),
                });
            } catch (err) {
                console.error("Failed to fetch proposal flags:", err);
                setProposalFlags({ clientSignatureRequired: false, pauseRequired: false });
            }
        };

        fetchProposalFlags();
    }, [open, selectedTask?.ProposalId]);

    const formatTotalHours = (actualStartDate, actualStartTime, actualEndTime, breakMinutes = 0, t) => {
        if (!actualStartDate || !actualStartTime || !actualEndTime) {
            return { formatted: `0${t("hourUnit")} 0${t("minutes")}`, hours: 0, minutes: 0 };
        }

        const start = moment(`${actualStartDate}T${moment(actualStartTime).format("HH:mm:ss")}`);
        const todayDate = moment().format("YYYY-MM-DD");
        const end = moment(`${todayDate}T${moment(actualEndTime).format("HH:mm")}:${start.format("ss")}`);

        let diffMinutes = Math.round(end.diff(start, "seconds") / 60);
        if (diffMinutes < 0) diffMinutes += 24 * 60;

        diffMinutes = Math.max(0, diffMinutes - breakMinutes);

        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        return {
            formatted: `${hours}${t("hourUnit")} ${minutes}${t("minutes")}`,
            hours,
            minutes
        };
    };

    const middleSubtasks = selectedTask?.Subtasks || [];

    const bottomSubtasks = [
        { SubtaskId: "static-bottom-1", Subtask: t("subtasks.finalInspection") },
        { SubtaskId: "static-bottom-2", Subtask: t("subtasks.complete") }
    ];

    const allWorkSteps = [...topSubtasks, ...middleSubtasks, ...bottomSubtasks];
    const hasActualStartForProgress =
    !!selectedTask?.ActualStartTime || Number(selectedTask?.Status) === 1;
    const hasActualEndForProgress = !!selectedTask?.ActualEndTime;
    const completedStepsCount = allWorkSteps.reduce((count, step, idx) => {
        const sId = step.SubtaskId || step.ProposalSubtaskId || `step-${idx}`;
        const isMiddleSubtask = idx >= topSubtasks.length && idx < topSubtasks.length + middleSubtasks.length;

        const done = isMiddleSubtask
            ? !!stepCompletions[sId]
            : idx < topSubtasks.length
                ? hasActualStartForProgress
                : hasActualEndForProgress || !!stepCompletions[sId];

        return count + (done ? 1 : 0);
    }, 0);
    const totalStepsCount = allWorkSteps.length;
    const progressPercent = totalStepsCount > 0 ? Math.round((completedStepsCount / totalStepsCount) * 100) : 0;
    const isAllStepsComplete = progressPercent === 100;

    const hasJobStartedLate = (selectedTask) => {
        if (!selectedTask?.StartTime || !selectedTask?.ActualStartTime || !selectedTask?.ActualStartDate)
            return false;

        const taskDate = moment(selectedTask.WeekStartDate || selectedTask.Date);
        if (taskDate.isAfter(moment(), "day")) return false;

        const taskDateStr = moment(selectedTask.WeekStartDate || selectedTask.Date).format("YYYY-MM-DD");
        const actualDateStr = moment(selectedTask.ActualStartDate).format("YYYY-MM-DD");

        if (actualDateStr < taskDateStr) return false;
        if (actualDateStr > taskDateStr) return true;

        const scheduledTime = moment.utc(`${taskDateStr}T${moment.utc(selectedTask.StartTime).format("HH:mm:ss")}`).local().format("HH:mm");
        const actualTime = moment.utc(selectedTask.ActualStartTime).local().format("HH:mm");
        return actualTime > scheduledTime;
    };

    const frequencyTranslationMap = {
        Daily: t("daily"),
        Weekly: t("weekly"),
        Monthly: t("monthly"),
        OneTime: t("oneTime")
    };

    const frequencyValue = selectedTask?.ProposalTask?.Frequency
        || selectedTask?.Recurrence?.frequency
        || selectedTask?.Frequency;

    const syncRecommendedEmployees = async (employeeIds) => {
        const requestId = ++recommendationRequestRef.current;
        if (!employeeIds?.length || !allEmployees.length) {
            if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups(null);
            return;
        }
        const values = getValues();
        const customerId = localStorage.getItem("customerId");
        const availabilityArgs = {
            conflictMap,
            startDate: moment(values.plannedStartDate).format("YYYY-MM-DD"),
            endDate: moment(values.plannedStartDate).format("YYYY-MM-DD"),
            occurrenceDates: [moment(values.plannedStartDate)],
            timeWindow: {
                startTimeOfDay: moment(values.plannedStartTime),
                endTimeOfDay: moment(values.plannedEndTime),
            },
            inFormSlots: [],
            customerId,
            excludeOrderDetailId: selectedTask?.OrderDetailId,
        };
        try {
            const recommendations = await fetchRecommendedEmployees(customerId, employeeIds);
            if (!recommendations) {
                if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(allEmployees) });
                return;
            }
            const groups = await partitionRecommendedEmployees(allEmployees, recommendations, (employee) =>
                checkEmployeeAvailability({ employeeId: employee.UserId, ...availabilityArgs })
            );
            if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups(groups);
        } catch (error) {
            if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(allEmployees) });
        }
    };

    const scheduleRecommendedEmployees = (employeeIds) => {
        clearTimeout(recommendationDebounceRef.current);
        recommendationDebounceRef.current = setTimeout(() => {
            void syncRecommendedEmployees(employeeIds);
        }, 275);
    };

    useEffect(() => {
        if (!open || !selectedTask) return;

        const normalized = normalizePlannedDateTime(selectedTask);

        reset({
            plannedEmployeeId: selectedTask.CleanerId || "",
            ...normalized
        });
        if (selectedTask.CleanerId) scheduleRecommendedEmployees([selectedTask.CleanerId]);
    }, [open, selectedTask, reset]);

    const handleDateTimeSave = async () => {
        const valid = await trigger([
            "plannedEmployeeId",
            "plannedStartDate",
            "plannedStartTime",
            "plannedEndTime"
        ]);

        if (!valid) return;

        const conflictCheckValues = getValues();
        const dateStrCheck = moment(conflictCheckValues.plannedStartDate).format("YYYY-MM-DD");
        const employeeName = allEmployees.find(e => e.UserId === conflictCheckValues.plannedEmployeeId)?.Name || conflictCheckValues.plannedEmployeeId;

        const issues = [];

        const conflictResult = await checkEmployeeAvailability({
            employeeId: conflictCheckValues.plannedEmployeeId,
            conflictMap,
            startDate: dateStrCheck,
            endDate: dateStrCheck,
            occurrenceDates: [moment(dateStrCheck)],
            timeWindow: {
                startTimeOfDay: moment(conflictCheckValues.plannedStartTime),
                endTimeOfDay: moment(conflictCheckValues.plannedEndTime),
            },
            inFormSlots: [],
            customerId: localStorage.getItem("customerId"),
            excludeOrderDetailId: selectedTask?.OrderDetailId,
        });

        if (conflictResult?.type === "leave") {
            issues.push({
                type: "leave",
                employeeId: conflictCheckValues.plannedEmployeeId,
                employeeName,
                leaveType: conflictResult.leaveType,
                leaveStatus: conflictResult.leaveStatus,
            });
        }

        if (conflictResult?.type === "taskConflict") {
            issues.push({
                type: "taskConflict",
                employeeId: conflictCheckValues.plannedEmployeeId,
                employeeName,
                conflictTaskName: conflictResult.conflictTaskName,
                conflictClientName: conflictResult.conflictClientName,
                conflictStart: conflictResult.conflictStart,
                conflictEnd: conflictResult.conflictEnd,
            });
        }

        if (conflictResult?.type === "combined") {
            conflictResult.issues.forEach((issue) => {
                if (issue.type === "leave") {
                    issues.push({
                        type: "leave",
                        employeeId: conflictCheckValues.plannedEmployeeId,
                        employeeName,
                        leaveType: issue.leaveType,
                        leaveStatus: issue.leaveStatus,
                    });
                }
                if (issue.type === "taskConflict") {
                    issues.push({
                        type: "taskConflict",
                        employeeId: conflictCheckValues.plannedEmployeeId,
                        employeeName,
                        conflictTaskName: issue.conflictTaskName,
                        conflictClientName: issue.conflictClientName,
                        conflictStart: issue.conflictStart,
                        conflictEnd: issue.conflictEnd,
                    });
                }
            });
        }

        if (moment(conflictCheckValues.plannedStartDate).isBefore(moment(), "day")) {
            const originalEmployeeName =
                allEmployees.find(e => e.UserId === selectedTask?.CleanerId)?.Name
                || selectedTask?.CleanerId;

            issues.push({
                type: "pastDateAssign",
                employeeId: conflictCheckValues.plannedEmployeeId,
                employeeName,
                fromEmployee: originalEmployeeName,
                toEmployee: employeeName,
            });
        }

        if (issues.length >= 1) {
            setPendingCombinedIssues(issues);
            setShowCombinedIssuesModal(true);
            return;
        }

        await proceedDateTimeSave();
    };

    const proceedDateTimeSave = async () => {
        try {
            const values = getValues();

            const isJobCompleted =
                selectedTask?.ActualStartDate &&
                selectedTask?.ActualEndDate &&
                selectedTask?.ActualStartTime &&
                selectedTask?.ActualEndTime;

            const dateStr = moment(values.plannedStartDate).format("YYYY-MM-DD");

            const localStart = moment(
                `${dateStr}T${moment(values.plannedStartTime).format("HH:mm")}`,
                "YYYY-MM-DDTHH:mm"
            );

            const localEnd = moment(
                `${dateStr}T${moment(values.plannedEndTime).format("HH:mm")}`,
                "YYYY-MM-DDTHH:mm"
            );

            const startIso = localStart.toISOString();
            const endIso = localEnd.toISOString();

            const durationMinutes = Math.round(
                moment(endIso).diff(moment(startIso), "minutes")
            );

            const updates = {
                StartTime: startIso,
                EndTime: endIso,
                WeekStartDate: localStart.format("YYYY-MM-DD"),
                DayOfWeek: localStart.format("dddd"),
                DurationMinutes: durationMinutes,
                CleanerId: values.plannedEmployeeId
            };

            const response = await axios.patch(
                `${process.env.REACT_APP_API_URL}/orderRoute/updateOrderDetail`,
                {
                    orderDetailId: selectedTask.OrderDetailId,
                    updates,
                    reopenCompletedJob: isJobCompleted,
                    performedBy: localStorage.getItem("UserId")
                },
                {
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
            );

            if (response.status === 200) {
                clearEmployeeAvailabilityCache();
                toast.success(t("taskDetailsUpdatedSuccessfully"), {
                    position: "top-right",
                    autoClose: 3000,
                });

                setEditingField(null);
                setSelectedEmployee(null);

                const oldCleanerId = selectedTask.CleanerId;
                const newCleanerId = values.plannedEmployeeId;
                const isEmployeeChanged = oldCleanerId !== newCleanerId;
                const userId = localStorage.getItem("UserId");
                const customerId = localStorage.getItem("customerId");
                const culture = localStorage.getItem("culture") || "nl";
                const sourceDate = moment(values.plannedStartDate).format("YYYY-MM-DD");

                let frequencyLabel = "";
                try {
                    const proposalTasksRes = await axios.get(
                        `${process.env.REACT_APP_API_URL}/proposalRoute/${selectedTask.ProposalId}/tasks`,
                        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                    );
                    const matchedTask = (proposalTasksRes.data || []).find(
                        t => t.ProposalTaskId === selectedTask.ProposalTaskId
                    );
                    if (matchedTask) {
                        const freq = matchedTask.Frequency || "";
                        const freqLower = freq.toLowerCase();
                        let selectedDays = [];
                        let advancedRecurrence = null;
                        try {
                            const raw = matchedTask.SelectedDays;
                            if (Array.isArray(raw)) {
                                selectedDays = raw;
                            } else if (typeof raw === "string") {
                                const parsed = JSON.parse(raw);
                                if (Array.isArray(parsed)) {
                                    selectedDays = parsed;
                                } else if (parsed && typeof parsed === "object") {
                                    advancedRecurrence = parsed;
                                }
                            }
                        } catch { }
                        const occurrenceCount = getOccurrenceCount({ frequency: freq, selectedDays, advancedRecurrence });
                        frequencyLabel = freq === "OneTime" ? t("onetime") : `${occurrenceCount}x ${t(freqLower)}`;
                    }
                } catch (freqErr) {
                    console.error("Failed to fetch frequency:", freqErr);
                }

                const location = [
                    selectedTask.Relation?.StreetName,
                    selectedTask.Relation?.PostCode,
                    selectedTask.Relation?.City,
                ].filter(Boolean).join(", ");

                const orderInfo = {
                    clientName: selectedTask.Relation?.Name,
                    location,
                    startDate: sourceDate,
                    endDate: sourceDate,
                    tasks: [{
                        taskName: selectedTask.TaskName || selectedTask.Task?.TaskName,
                        startTime: moment(values.plannedStartTime).format("HH:mm"),
                        endTime: moment(values.plannedEndTime).format("HH:mm"),
                        frequency: frequencyLabel,
                    }],
                };

                const commonData = {
                    orderInfo,
                    culture,
                    organizationName: process.env.REACT_APP_ORGANIZATION_NAME,
                    userId,
                    raisedFrom: userId,
                    sourceId: selectedTask.OrderId,
                    sourceType: "order",
                    sourceDate,
                };
                // Assigned/updated notifications point at the exact task so the mobile app opens that one.
                const taskTarget = { sourceId: selectedTask.OrderDetailId, sourceType: "task" };

                try {
                    if (isEmployeeChanged) {
                        const oldCleaner = allEmployees.find(e => e.UserId === oldCleanerId);
                        const newCleaner = allEmployees.find(e => e.UserId === newCleanerId);

                        await dispatchBatchNotification({
                            customerId,
                            eventKey: "TASK_UNASSIGNED",
                            entries: [{
                                cleaner: {
                                    userId: oldCleanerId,
                                    name: oldCleaner?.Name,
                                    email: oldCleaner?.Emailaddress,
                                    phone: oldCleaner?.PhoneNumber,
                                },
                                data: {
                                    ...commonData,
                                    isActionRequired: false,
                                    inAppMessageCleaner: t("in-AppNotifications.taskRemovedMessage", {
                                        taskName: selectedTask.TaskName || selectedTask.Task?.TaskName,
                                        clientName: selectedTask.Relation?.Name,
                                        date: moment(values.plannedStartDate).format("DD-MM-YYYY"),
                                    }),
                                    inAppManagerSuffix: t("in-AppNotifications.orderUnassignedManagerSuffix", {
                                        clientName: selectedTask.Relation?.Name,
                                    }),
                                }
                            }]
                        });

                        await dispatchBatchNotification({
                            customerId,
                            eventKey: "TASK_ASSIGNED",
                            entries: [{
                                cleaner: {
                                    userId: newCleanerId,
                                    name: newCleaner?.Name,
                                    email: newCleaner?.Emailaddress,
                                    phone: newCleaner?.PhoneNumber,
                                },
                                data: {
                                    ...commonData,
                                    ...taskTarget,
                                    isActionRequired: true,
                                    inAppMessageCleaner: t("in-AppNotifications.taskAssignedMessage", {
                                        taskName: selectedTask.TaskName || selectedTask.Task?.TaskName,
                                        clientName: selectedTask.Relation?.Name,
                                    }),
                                    inAppManagerSuffix: t("in-AppNotifications.taskAssignedManagerSuffix", {
                                        taskName: selectedTask.TaskName || selectedTask.Task?.TaskName,
                                        clientName: selectedTask.Relation?.Name,
                                    }),
                                }
                            }]
                        });
                    } else {
                        const cleaner = allEmployees.find(e => e.UserId === oldCleanerId);

                        await dispatchBatchNotification({
                            customerId,
                            eventKey: "TASK_UPDATED",
                            entries: [{
                                cleaner: {
                                    userId: oldCleanerId,
                                    name: cleaner?.Name,
                                    email: cleaner?.Emailaddress,
                                    phone: cleaner?.PhoneNumber,
                                },
                                data: {
                                    ...commonData,
                                    ...taskTarget,
                                    isActionRequired: true,
                                    inAppMessageCleaner: t("in-AppNotifications.taskRescheduledMessage", {
                                        taskName: selectedTask.TaskName || selectedTask.Task?.TaskName,
                                        clientName: selectedTask.Relation?.Name,
                                        date: moment(values.plannedStartDate).format("DD-MM-YYYY"),
                                        startTime: moment(values.plannedStartTime).format("HH:mm"),
                                        endTime: moment(values.plannedEndTime).format("HH:mm"),
                                    }),
                                    inAppManagerSuffix: t("in-AppNotifications.orderRescheduledManagerSuffix", {
                                        clientName: selectedTask.Relation?.Name,
                                    }),
                                }
                            }]
                        });
                    }
                } catch (notifErr) {
                    console.error("Notification dispatch failed (non-blocking):", notifErr);
                }

                if (context === "calendar" && calendarStart && calendarEnd) {
                    await fetchCalendarData(calendarStart, calendarEnd);
                }

                if (context === "orderDetails" && typeof fetchOrderDetails === "function") {
                    await fetchOrderDetails();
                }

                if (context === "jobs" && typeof onTaskUpdated === "function") {
                    await onTaskUpdated();
                }

            } else {
                toast.error(t("taskDetailsUpdateFailed"), {
                    position: "top-right",
                    autoClose: 3000,
                });
            }
        } catch (err) {
            console.error("Failed to update planned date/time:", err);
            toast.error(t("taskDetailsUpdateFailed"), {
                position: "top-right",
                autoClose: 3000,
            });
        }
    };

    useEffect(() => {
        if (!open) {
            resetPlannedFields();
            setEditingField(null);
            setSelectedEmployee(null);
            setStepCompletions({});
            setStepSaving({});
            setCompletionImages([]);
            setProposalFlags({ clientSignatureRequired: false, pauseRequired: false });
            setExistingReview(null);
        }
    }, [open]);

    const handleStepToggle = async (proposalSubtaskId) => {
        if (userRole !== "Employee" && !isEmployeeView) return;

        const isAlreadyDone = !!stepCompletions[proposalSubtaskId];
        const userId = localStorage.getItem("UserId");

        setStepSaving((prev) => ({ ...prev, [proposalSubtaskId]: true }));

        try {
            if (isAlreadyDone) {
                await axios.delete(
                    `${process.env.REACT_APP_API_URL}/orderDetailSubtaskCompletionRoute/uncomplete`,
                    {
                        data: {
                            orderDetailId: selectedTask.OrderDetailId,
                            proposalSubtaskId,
                        },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                    }
                );
                setStepCompletions((prev) => {
                    const next = { ...prev };
                    delete next[proposalSubtaskId];
                    stepCompletionsCache.current[selectedTask.OrderDetailId] = next;
                    if (typeof onStepCompletionsChange === "function") {
                        onStepCompletionsChange(selectedTask.OrderDetailId, next);
                    }
                    return next;
                });
            } else {
                const res = await axios.post(
                    `${process.env.REACT_APP_API_URL}/orderDetailSubtaskCompletionRoute/complete`,
                    {
                        orderDetailId: selectedTask.OrderDetailId,
                        proposalSubtaskId,
                        employeeId: userId,
                    },
                    { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                );
                setStepCompletions((prev) => {
                    const next = {
                        ...prev,
                        [proposalSubtaskId]: {
                            CompletedAt: res.data.CompletedAt,
                            EmployeeId: res.data.EmployeeId,
                        },
                    };
                    stepCompletionsCache.current[selectedTask.OrderDetailId] = next;
                    if (typeof onStepCompletionsChange === "function") {
                        onStepCompletionsChange(selectedTask.OrderDetailId, next);
                    }
                    return next;
                });
            }
        } catch (err) {
            console.error("Failed to toggle step:", err);
            toast.error(t("stepToggleError"), { position: "top-right" });
        } finally {
            setStepSaving((prev) => ({ ...prev, [proposalSubtaskId]: false }));
        }
    };

    // const extractClientSignature = () => {
    //     if (!proposalFlags.clientSignatureRequired) {
    //         return null;
    //     }
    //     if (tempClientSignature) {
    //         return tempClientSignature;
    //     }
    //     toast.error(t("pleaseEnterSignature"), {
    //         position: "top-right",
    //         autoClose: 3000,
    //     });
    //     return undefined;
    // };

    const extractClientSignature = () => {
        return tempClientSignature || null;
    };

    const handleClientSignatureSave = (base64) => {
        setTempClientSignature(base64);
        setShowSignatureModal(false);
    };

    useEffect(() => {
        if (!selectedTask?.OrderDetailId || !selectedTask?.ActualStartTime) return;
        fetchStepCompletions(selectedTask.OrderDetailId);
    }, [selectedTask?.ActualStartTime, selectedTask?.OrderDetailId]);

    // Helper functions for photo upload
    const validateAndProcessImage = (file) => {
        if (!ALLOWED_FILE_TYPES.includes(file.type.toLowerCase())) {
            toast.error(t("invalidImageFormat"), { position: "top-right" });
            return null;
        }
        return file;
    };

    const processImageFile = async (file) => {
        const validatedFile = validateAndProcessImage(file);
        if (!validatedFile) return null;

        let fileToUse = validatedFile;

        if (validatedFile.size > MAX_FILE_SIZE) {
            try {
                const compressed = await imageCompression(validatedFile, COMPRESSION_OPTIONS);
                fileToUse = new File([compressed], validatedFile.name, {
                    type: compressed.type,
                    lastModified: Date.now(),
                });
            } catch (err) {
                toast.error(t('imageCompressionError'), { position: 'top-right' });
                return null;
            }
        }

        return fileToUse;
    };

    const handlePhotoCapture = async (file) => {
        const fileToAdd = await processImageFile(file);
        if (!fileToAdd) return;

        setTaskPhotos(prev => {
            const currentTotalSize = prev.reduce((sum, f) => sum + f.size, 0);
            if (currentTotalSize + fileToAdd.size > MAX_PAYLOAD_SIZE) {
                toast.error(
                    t('totalPayloadExceeded', { maxSize: MAX_PAYLOAD_SIZE_MB }),
                    { position: 'top-right' }
                );
                return prev;
            }

            return [...prev, fileToAdd];
        });
    };

    const handlePhotoRemove = (indexToRemove) => {
        setTaskPhotos(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleFeedbackImageAdd = async (file) => {
        const processed = await processImageFile(file);
        if (!processed) return;

        setFeedbackImages(prev => {
            const currentTotalSize = prev.reduce((sum, f) => sum + f.size, 0);
            if (currentTotalSize + processed.size > MAX_PAYLOAD_SIZE) {
                toast.error(
                    t('totalPayloadExceeded', { maxSize: MAX_PAYLOAD_SIZE_MB }),
                    { position: 'top-right' }
                );
                return prev;
            }
            return [...prev, processed];
        });
    };

    const handleFeedbackImageRemove = (indexToRemove) => {
        setFeedbackImages(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const handleComplaintImageAdd = async (file) => {
        if (!selectedComplaintReason) {
            setComplaintReasonError(true);
            toast.error(t("pleaseSelectReasonFirst") || "Please select a reason first", { position: "top-right" });
            return;
        }
        if (!file) return;

        const processed = await processImageFile(file);
        if (!processed) return;

        const totalSize = complaintImages.reduce((sum, f) => sum + f.size, 0) + processed.size;
        if (totalSize > MAX_PAYLOAD_SIZE) {
            toast.error(t('totalPayloadExceeded', { maxSize: MAX_PAYLOAD_SIZE_MB }), { position: 'top-right' });
            return;
        }

        setComplaintImages(prev => [...prev, processed]);
    };

    const handleComplaintImageRemove = (indexToRemove) => {
        setComplaintImages(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

             const handleComplaintSubmit = async () => {
                if (!selectedComplaintReason) {
            setComplaintReasonError(true);
            return;
        }
        setComplaintReasonError(false);

        if (selectedComplaintReason === "otherWithComments" && !complaintComment.trim()) {
            toast.error(t("pleaseAddDetailsForOther") || "Please add details when selecting Other", { position: "top-right" });
            return;
        }

        setIsSubmittingComplaint(true);
        try {
            const customerId = localStorage.getItem("customerId") || selectedTask?.CustomerId || "";

            const images = await Promise.all(
                complaintImages.map((photo) => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            let ext = photo.type.split("/")[1] || "jpg";
                            if (ext === "jpeg") ext = "jpg";

                            resolve({
                                base64: reader.result.split(",")[1],
                                extension: ext,
                            });
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(photo);
                    });
                })
            );

            const taskDateISO = moment(selectedTask?.WeekStartDate || selectedTask?.Date).format("YYYY-MM-DD");

            const payload = {
                orderDetailId: selectedTask.OrderDetailId,
                customerId,
                raisedFromUserId: localStorage.getItem("UserId"),
                complaintReason: selectedComplaintReason,
                comment: complaintComment.trim(),
                images,
                relationId: selectedTask?.RelationId || selectedTask?.Relation?.RelationId || relation?.RelationId || null,
                taskName: selectedTask?.Task?.TaskName?.trim() || selectedTask?.TaskName || "",
                taskDateISO,
                clientName: selectedTask?.Relation?.Name || relation?.Name || "",
                culture: localStorage.getItem("culture") || "nl",
            };

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/orderDetailComplaintRoute/save`,
                payload,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            toast.success(t("complaintRaisedSuccessfully") || "Complaint raised successfully", { position: "top-right" });

            setExistingComplaint(res.data.complaint);
            complaintCache.current[selectedTask.OrderDetailId] = res.data.complaint;
            setSelectedComplaintReason("");
            setComplaintComment("");
            setComplaintImages([]);
        } catch (err) {
            console.error("Failed to submit complaint:", err);
           toast.error(t("complaintSubmitError"), { position: "top-right" });
        } finally {
            setIsSubmittingComplaint(false);
        }
    };

    const handleNoteImageAdd = async (file) => {
        const processed = await processImageFile(file);
        if (!processed) return;

        setSelectedNoteImages(prev => {
            const currentTotalSize = prev.reduce((sum, f) => sum + f.size, 0);
            if (currentTotalSize + processed.size > MAX_PAYLOAD_SIZE) {
                toast.error(
                    t('totalPayloadExceeded', { maxSize: MAX_PAYLOAD_SIZE_MB }),
                    { position: 'top-right' }
                );
                return prev;
            }
            return [...prev, processed];
        });
    };

    const handleNoteImageRemove = (indexToRemove) => {
        setSelectedNoteImages(prev => prev.filter((_, i) => i !== indexToRemove));
    };

    const handleAddNote = async () => {
        if (!newNoteText.trim() && selectedNoteImages.length === 0) return;
        const totalNotePayloadSize = selectedNoteImages.reduce((sum, f) => sum + f.size, 0);
        if (totalNotePayloadSize > MAX_PAYLOAD_SIZE) {
            toast.error(
                t('totalPayloadExceeded', { maxSize: MAX_PAYLOAD_SIZE_MB }),
                { position: 'top-right' }
            );
            return;
        }
        setIsSubmittingNote(true);

        if (selectedNoteImages.some(img => img.size > MAX_FILE_SIZE)) {
            toast.error(t('imageCompressionError'), { position: 'top-right' });
            setIsSubmittingNote(false);
            return;
        }
        try {
            const authorId = localStorage.getItem("UserId");

            const images = await Promise.all(
                selectedNoteImages.map((photo) => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve({
                        base64: reader.result.split(',')[1],
                        extension: photo.type.split('/')[1],
                    });
                    reader.onerror = reject;
                    reader.readAsDataURL(photo);
                }))
            );

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/taskDiscussionRoute/note/save`,
                {
                    orderDetailId: selectedTask.OrderDetailId,
                    authorId,
                    noteText: newNoteText.trim(),
                    images,
                    customerId: localStorage.getItem("customerId")
                },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            setTaskNotes(prev => {
                if (prev.some(n => n.TaskDiscussionId === res.data.note.TaskDiscussionId)) return prev;
                return [res.data.note, ...prev];
            });

            setNewNoteText("");
            setSelectedNoteImages([]);
        } catch (err) {
            console.error("Failed to add note:", err);
            toast.error(t("noteAddError"), { position: "top-right" });
        } finally {
            setIsSubmittingNote(false);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isPayloadFull) setIsDragOver(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);

        if (isPayloadFull) return;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            Array.from(files).forEach((file) => handlePhotoCapture(file));
        }
    };

    const uploadPhotosToServer = async (orderDetailId) => {
        if (taskPhotos.length === 0) return true;

        try {
            const images = await Promise.all(
                taskPhotos.map((photo) => {
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            const base64 = reader.result.split(',')[1];
                            const extension = photo.type.split('/')[1];
                            resolve({ base64, extension });
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(photo);
                    });
                })
            );

            await axios.post(
                `${process.env.REACT_APP_API_URL}/orderDetailCompletionImageRoute/save`,
                { orderDetailId, images },
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            imageCache.current[orderDetailId] = null;
            return true;

        } catch (error) {
            console.error("Photo upload failed:", error);
            toast.error(t("photoUploadError"), { position: "top-right" });
            return false;
        }
    };

    const saveJobCompletion = async (task, formValues, noteValue, triggerFn, signature) => {
        try {
            setIsSaving(true);

            // Use different validation for employee view vs admin view
            if (userRole === "Employee" || isEmployeeView) {

                // For employee view, validate only job completion fields, not planned fields
                const validationFields = isOverdueTask
                    ? ["editStartDate", "editEndDate", "editStartTime", "editEndTime", "status"]
                    : ["actualEndTime", "status"];

                const valid = await triggerFn(validationFields);

                if (!valid) {
                    return false;
                }
            } else {
                // For admin view, use full validation
                const valid = await triggerFn();
                if (!valid) return false;
            }

            const values = getValues();
            let startTimeUTC, endTimeUTC;

            if (isOverdueTask) {
                const startDate = moment(values.editStartDate);
                const endDate = moment(values.editEndDate);
                const startTime = moment(values.editStartTime);
                const endTime = moment(values.editEndTime);

                const fullStart = moment(`${startDate.format("YYYY-MM-DD")}T${startTime.format("HH:mm")}`);
                const fullEnd = moment(`${endDate.format("YYYY-MM-DD")}T${endTime.format("HH:mm")}`);

                startTimeUTC = fullStart.clone().utc();
                endTimeUTC = fullEnd.clone().utc();
            } else {
                const taskDateStr = moment(
                    selectedTask?.WeekStartDate || selectedTask?.Date || new Date()
                ).format("YYYY-MM-DD");

                const startTime = moment(values.actualStartTime);
                const endTime = moment(values.actualEndTime);

                const fullStart = moment(`${taskDateStr}T${startTime.format("HH:mm")}`);
                const fullEnd = moment(`${taskDateStr}T${endTime.format("HH:mm")}`);

                startTimeUTC = fullStart.clone().utc();
                endTimeUTC = fullEnd.clone().utc();
            }
            const status = values.status;
            const customerId = localStorage.getItem("customerId");
            const loggedInUserId = localStorage.getItem("UserId");

            const rawMinutes = endTimeUTC.diff(startTimeUTC, "minutes");
            const totalMinutes = Math.max(0, rawMinutes - breakMinutes);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const totalHoursFormatted = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;

            let uploadedPhotoUrls = [];
            if (taskPhotos.length > 0) {
                const photosSaved = await uploadPhotosToServer(task.OrderDetailId);
                if (!photosSaved) {
                    setIsSaving(false);
                    return false;
                }
            }

            let orderUpdatePayload;
            if (isOverdueTask) {
                orderUpdatePayload = {
                    orderDetailId: task.OrderDetailId,
                    updates: {
                        ActualStartTime: startTimeUTC.toISOString(),
                        ActualStartDate: startTimeUTC.format("YYYY-MM-DD"),
                        ActualEndTime: endTimeUTC.toISOString(),
                        ActualEndDate: endTimeUTC.format("YYYY-MM-DD"),
                        Status: status,
                        Notes: noteValue || "",
                        ClientSignature: signature
                    }
                };
            } else {
                orderUpdatePayload = {
                    orderDetailId: task.OrderDetailId,
                    updates: {
                        ActualStartTime: startTimeUTC.toISOString(),
                        ActualStartDate: startTimeUTC.format("YYYY-MM-DD"),
                        ActualEndTime: endTimeUTC.toISOString(),
                        ActualEndDate: endTimeUTC.format("YYYY-MM-DD"),
                        Status: status,
                        Notes: noteValue || "",
                        ClientSignature: signature
                    }
                };
            }

            const hourRegistrationPayload = {
                CustomerId: customerId,
                OrderId: task.OrderId,
                OrderDetailId: task.OrderDetailId,
                EmployeeId: task.CleanerId,
                LocationId: task.Order.Location,
                ClientId: task.Relation.RelationId,
                ActualStartDateTime: startTimeUTC.toISOString(),
                ActualEndDateTime: endTimeUTC.toISOString(),
                BreakTime: breakMinutes,
            };

            await axios.patch(
                `${process.env.REACT_APP_API_URL}/orderRoute/updateOrderDetail`,
                orderUpdatePayload,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
            clearEmployeeAvailabilityCache();

            await axios.post(
                `${process.env.REACT_APP_API_URL}/hourRegistrationRoute/addHours`,
                hourRegistrationPayload,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            await checkAndUpdateOrderStatus(task.OrderId);

            toast.success(t("jobCompletedSuccessfully"), { position: "top-right", autoClose: 3000 });
            
            if (isOverdueTask) {
                Object.assign(task, {
                    ActualStartTime: startTimeUTC.toISOString(),
                    ActualStartDate: startTimeUTC.format("YYYY-MM-DD"),
                    ActualEndTime: endTimeUTC.toISOString(),
                    ActualEndDate: endTimeUTC.format("YYYY-MM-DD"),
                    Status: status,
                    Notes: noteValue || ""
                });
            } else {
                Object.assign(task, {
                    ActualStartTime: startTimeUTC.toISOString(),
                    ActualStartDate: startTimeUTC.format("YYYY-MM-DD"),
                    ActualEndTime: endTimeUTC.toISOString(),
                    ActualEndDate: endTimeUTC.format("YYYY-MM-DD"),
                    Status: status,
                    Notes: noteValue || ""
                });
            }

            setIsEditingJob(false);
            setIsEditingEndJob(false);
            setTaskPhotos([]);

            if (typeof fetchEmployeeTasks === "function") {
                await fetchEmployeeTasks();
            }
            if (typeof onTaskUpdated === "function") {
                await onTaskUpdated();
            }

            onClose();

            const resolveCleanerForNotification = async () => {
                if (task.Cleaner?.Name) return task.Cleaner;

                const fromList = allEmployees.find(e => e.UserId === task.CleanerId);
                if (fromList?.Name) return fromList;
                try {
                    const res = await axios.get(
                        `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${task.CleanerId}`,
                        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                    );
                    return res.data || {};
                } catch (err) {
                    console.error("Could not resolve cleaner for notification:", err);
                    return {};
                }
            };

            const cleaner = await resolveCleanerForNotification();
            const dispatchUserId = localStorage.getItem("UserId");
            const dispatchCulture = localStorage.getItem("culture") || "nl";

            const taskStartDateTime = moment(startTimeUTC).local().format("DD-MM-YYYY HH:mm");
            const taskEndDateTime = moment(endTimeUTC).local().format("DD-MM-YYYY HH:mm");
            const locationParts = [
                task.Relation?.StreetName,
                task.Relation?.HouseNumber,
                task.Relation?.PostCode,
                task.Relation?.City,
            ].filter(Boolean).join(", ");

            dispatchBatchNotification({
                customerId,
                eventKey: "TASK_COMPLETED",
                entries: [{
                    cleaner: {
                        userId: task.CleanerId,
                        name: cleaner?.Name,
                        email: cleaner?.Emailaddress,
                        phone: cleaner?.PhoneNumber,
                    },
                    data: {
                        clientName: task.Relation?.Name,
                        taskName: task.Task?.TaskName,
                        clientAddress: `${task.Relation?.StreetName || ""} ${task.Relation?.HouseNumber || ""}`,
                        clientTown: `${task.Relation?.PostCode || ""} ${task.Relation?.City || ""}`,
                        workType: task.TemplateName,
                        taskStartDateTime,
                        taskEndDateTime,
                        employeeNames: cleaner?.Name || "",
                        employeePhoneNumber: cleaner?.PhoneNumber || "",
                        culture: dispatchCulture,
                        userId: dispatchUserId,
                        raisedFrom: dispatchUserId,
                        isActionRequired: false,
                        sourceId: task.OrderDetailId,
                        sourceType: "task",
                        sourceDate: task.WeekStartDate,
                        inAppManagerSuffix: t("in-AppNotifications.taskCompletedManagerSuffix", {
                            clientName: task.Relation?.Name,
                        }),
                        whatsappTemplateName: `task_completion_notification_marketing_${dispatchCulture}`,
                        whatsappParameters: [
                            "{{MANAGER_NAME}}",
                            task.Task?.TaskName,
                            cleaner?.Name || "",
                            taskEndDateTime,
                            locationParts,
                        ],
                    }
                }]
            }).catch(err => console.error("Job completion notification error (non-blocking):", err));

            return true;

        } catch (error) {
            console.error("Error completing job:", error);

            if (error.response?.status === 404) {
                toast.error(t("taskNotExist"), { position: "top-right" });
                setTimeout(() => fetchEmployeeTasks(), 2000);
                if (typeof onClose === "function") onClose();
            } else {
                toast.error(t("jobCompletionError"), { position: "top-right" });
            }

            return false;
        } finally {
            setIsSaving(false);

        }
    };

    const isTaskInPast = (() => {
        if (!selectedTask) return false;

        const taskDate = moment(
            selectedTask.WeekStartDate || selectedTask.Date
        );

        // Past day only (today allowed)
        return taskDate.isBefore(moment(), "day");
    })();

    const isJobCompleted =
        !!selectedTask?.ActualStartDate &&
        !!selectedTask?.ActualEndDate &&
        !!selectedTask?.ActualStartTime &&
        !!selectedTask?.ActualEndTime;

    const getPlannedDuration = () => {
        const start = watch("plannedStartTime");
        const end = watch("plannedEndTime");

        if (!start || !end) return `0${t("hourUnit")}`;

        const diffMinutes = moment(end).diff(moment(start), "minutes");

        if (diffMinutes <= 0) return `0${t("hourUnit")}`;

        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        return `${hours}${t("hourUnit")}${minutes.toString().padStart(2, "0")}`;
    };

    const resetPlannedFields = () => {
        if (!selectedTask) return;

        const taskDateStr = moment(
            selectedTask.WeekStartDate || selectedTask.Date
        ).format("YYYY-MM-DD");

        const startTimeOnly = moment.utc(selectedTask.StartTime).format("HH:mm:ss");
        const endTimeOnly = moment.utc(selectedTask.EndTime).format("HH:mm:ss");

        const startLocal = moment.utc(`${taskDateStr}T${startTimeOnly}`).local();
        const endLocal = moment.utc(`${taskDateStr}T${endTimeOnly}`).local();

        reset({
            plannedEmployeeId: selectedTask.CleanerId || "",
            plannedStartDate: startLocal.toDate(),
            plannedStartTime: startLocal.toDate(),
            plannedEndTime: endLocal.toDate()
        });
    };

    const isPayloadFull = React.useMemo(() => {
        const currentPayloadSize = taskPhotos.reduce((sum, f) => sum + f.size, 0);
        return currentPayloadSize >= MAX_PAYLOAD_SIZE;
    }, [taskPhotos]);

    return (
        <>
            <Offcanvas
                show={open}
                onHide={onClose}
                placement="end"
                backdrop={true}
                scroll={true}
                className="task-offcanvas"
            >
                <div className="thin-scrollbar h-100 d-flex flex-column">
                    {loading || !selectedTask ? (
                        <div className="text-center my-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">{t("loading")}</span>
                            </Spinner>
                        </div>
                    ) : (
                        <>
                            <Offcanvas.Header className="px-3 py-2 border-bottom">
                                <div className="w-100">
                                    <Row className="align-items-center flex-nowrap g-0">
                                        <Col className="overflow-hidden">
                                            <h4 className="mb-1 text-truncate">
                                                {userRole === USER_ROLES.CLIENT
                                                    ? (selectedTask?.Task?.TaskName?.trim() || selectedTask?.TaskName)
                                                    : selectedTask?.Relation?.Name}
                                            </h4>
                                        </Col>

                                        <Col xs="auto" className="flex-shrink-0 ms-2">
                                            <div className="d-flex align-items-center gap-1">
                                                {renderStatusBadge(selectedTask?.Status, t)}
                                                {hasJobStartedLate(selectedTask) && (
                                                    <FontAwesomeIcon
                                                        icon={faExclamationCircle}
                                                        className="text-warning ms-1 cursor-pointer"
                                                        title={t("jobStartedLate")}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toast.warning(t("jobStartedLate"), {
                                                                position: "top-right"
                                                            });
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </Col>

                                        <Col xs="auto" className="flex-shrink-0 ms-2">
                                            <Button
                                                variant="link"
                                                className="p-0 border-0 shadow-none bg-transparent text-secondary fs-7"
                                                onClick={onClose}
                                                title={t("close")}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </Button>
                                        </Col>
                                    </Row>

                                    {/* Row 2: Location */}
                                    <Row className="align-items-center">
                                        <Col>
                                            <p className="fw-semi-bold data-font-size data-font-colour mb-0">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2  icon-opacity" />
                                                {userRole === 'Employee' || isEmployeeView ? (
                                                    <>
                                                        {relation?.StreetName || ''} {relation?.HouseNumber || ''}, {relation?.PostCode || ''} {relation?.City || ''}
                                                    </>
                                                ) : (
                                                    <>
                                                        {relation?.StreetName}, {relation?.City}
                                                    </>
                                                )}
                                            </p>
                                        </Col>
                                    </Row>
                                </div>
                            </Offcanvas.Header>

                            <Offcanvas.Body className="border-top px-3 py-2 overflow-auto order-details-card">
                            
                            <div className="d-flex align-items-center flex-wrap gap-2 mb-3 mt-3">
                                <h6 className="ls mb-0 fs-9">{selectedTask?.Task?.TaskName?.trim() || selectedTask?.TaskName}</h6>
                                {selectedTask?.TemplateName && (<SubtleBadge pill bg="primary" style={{ fontSize: "0.75rem" }}>{selectedTask.TemplateName}</SubtleBadge>)}
                                <SubtleBadge pill bg={getFrequencyPillColor(frequencyValue)} className="fw-semi-bold">
                                    {frequencyTranslationMap[frequencyValue] || frequencyValue}
                                </SubtleBadge>
                            </div>   

                            {userRole !== "Employee" && !isEmployeeView && (
                                <div className="position-relative mb-1">
                                    <div className={classNames("task-detail-card task-summary-card", {
                                        "task-summary-card-editing": editingField === "summary" && (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER)
                                    })}>
                                        {editingField === "summary" && (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER) ? (
                                            <div className="px-0 py-1">
                                                <div className="d-flex align-items-start gap-2">
                                                    <div className="avatar flex-shrink-0 task-summary-avatar task-summary-edit-avatar">
                                                        <div className={`avatar-name rounded-circle ${getDiscussionAvatarColorClass(
                                                            selectedEmployee?.Name || selectedTask?.Cleaner?.Name || selectedTask?.CleanerName || "?"
                                                        )} d-flex align-items-center justify-content-center w-100 h-100 fs-9`}>
                                                            <span className="fw-bold">
                                                                {(selectedEmployee?.Name || selectedTask?.Cleaner?.Name || selectedTask?.CleanerName || "--")
                                                                    .split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="flex-grow-1">
                                                        <Form.Label className="task-summary-field-label task-summary-edit-label mb-1">{t("employeeText")}</Form.Label>
                                                        <Controller
                                                            name="plannedEmployeeId"
                                                            control={control}
                                                            rules={{ required: t("employeeRequired") }}
                                                            render={({ field, fieldState: { error } }) => {
                                                                const flatEmployeeOptions = sortEmployeesAscending(allEmployees).map(emp => ({
                                                                    value: emp.UserId,
                                                                    label: emp.Name
                                                                }));
                                                                const employeeOptions = recommendedEmployeeGroups?.recommended?.length > 0 ? [
                                                                    { label: t("recommendedEmployees"), options: recommendedEmployeeGroups.recommended.map(emp => ({ value: emp.UserId, label: emp.Name })) },
                                                                    { label: t("allEmployees"), options: recommendedEmployeeGroups.others.map(emp => ({ value: emp.UserId, label: emp.Name })) },
                                                                ] : flatEmployeeOptions;
                                                                return (
                                                                    <>
                                                                        <Select
                                                                            placeholder={t("Selectemployee")}
                                                                            noOptionsMessage={() => t("noOptionsAvailable")}
                                                                            options={employeeOptions}
                                                                            isClearable
                                                                            className={`custom-select-container custom-select-sm ${error ? "is-invalid" : ""}`}
                                                                            classNamePrefix="custom-select"
                                                                            value={flatEmployeeOptions.find(opt => opt.value === field.value) || null}
                                                                            onChange={(selected) => {
                                                                                field.onChange(selected ? selected.value : "");
                                                                                handleEmployeeSelect(allEmployees.find(e => e.UserId === selected?.value));
                                                                                scheduleRecommendedEmployees(selected ? [selected.value] : []);
                                                                            }}
                                                                        />
                                                                        {error && <div className="invalid-feedback d-block">{error.message}</div>}
                                                                    </>
                                                                );
                                                            }}
                                                        />

                                                        <Row className="gy-3 gx-2 mt-2">
                                                            <Col xs={5}>
                                                                <Form.Label className="task-summary-field-label task-summary-edit-label mb-1">{t("date")}</Form.Label>
                                                                <Controller control={control} name="plannedStartDate"
                                                                    render={({ field }) => <FixedDatePicker field={field} error={errors.plannedStartDate} />} />
                                                            </Col>
                                                            <Col xs={3}>
                                                                <Form.Label className="task-summary-field-label task-summary-edit-label mb-1">{t("startTime")}</Form.Label>
                                                                <Controller control={control} name="plannedStartTime"
                                                                    render={({ field }) => <FixedTimePicker field={field} error={errors.plannedStartTime} />} />
                                                            </Col>
                                                            <Col xs={4}>
                                                                <Form.Label className="task-summary-field-label task-summary-edit-label mb-1">{t("endTime")}</Form.Label>
                                                                <Controller control={control} name="plannedEndTime"
                                                                    render={({ field }) => <FixedTimePicker field={field} error={errors.plannedEndTime} />} />
                                                            </Col>
                                                        </Row>

                                                        <div className="d-flex gap-2 mt-3 pt-3 border-top justify-content-end task-summary-edit-actions">
                                                            <Button size="sm" variant="outline-secondary" onClick={() => {
                                                                resetPlannedFields();
                                                                setEditingField(null);
                                                                setSelectedEmployee(null);
                                                            }}>{t("cancel")}</Button>
                                                            <Button size="sm" variant="primary" onClick={handleDateTimeSave}>{t("save")}</Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                className={classNames("task-summary-row d-flex align-items-center", {
                                                    "cursor-pointer": (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER) && !isJobCompleted
                                                })}
                                                onClick={() => {
                                                    if ((userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER) && !isJobCompleted) {
                                                        setEditingField("summary");
                                                    }
                                                }}
                                            >
                                                <div className="avatar flex-shrink-0 task-summary-avatar">
                                                    <div className={`avatar-name rounded-circle ${getDiscussionAvatarColorClass(
                                                        selectedEmployee?.Name || selectedTask?.Cleaner?.Name || selectedTask?.CleanerName || "?"
                                                    )} d-flex align-items-center justify-content-center w-100 h-100 fs-9`}>
                                                        <span className="fw-bold">
                                                            {(selectedEmployee?.Name || selectedTask?.Cleaner?.Name || selectedTask?.CleanerName || "--")
                                                                .split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="flex-grow-1 min-w-0 ms-2">
                                                    <div className="task-detail-value">
                                                        {selectedEmployee?.Name || selectedTask?.Cleaner?.Name || selectedTask?.CleanerName || "--"}
                                                    </div>
                                                    <div className="task-summary-datetime text-muted mt-1">
                                                        <FontAwesomeIcon icon={faClock} className="me-1 icon-opacity" />
                                                        {moment(watch("plannedStartDate")).format("DD-MM-YYYY")} | {moment(watch("plannedStartTime")).format("HH:mm")}
                                                        {" – "}{moment(watch("plannedEndTime")).format("HH:mm")}
                                                        <span className="task-detail-duration">({getPlannedDuration()})</span>
                                                    </div>
                                                </div>

                                                {(userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER) && !isJobCompleted && (
                                                    <FontAwesomeIcon icon={faPen} className="task-summary-edit-icon ms-2 flex-shrink-0" />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <Nav
                                variant="tabs"
                                activeKey={activeSection}
                                onSelect={(key) => {
                                    setActiveSection(key);
                                    if (key === "discussion") markDiscussionAsSeen();
                                }}
                                className="mb-3 mt-1 task-drawer-nav border-bottom"
                                fill
                            >
                                <Nav.Item>
                                    <Nav.Link eventKey="overview" className="cursor-pointer outline-none">
                                        {t("overview")}
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="discussion" className="cursor-pointer outline-none">
                                        {t("discussion")}
                                        {unreadNotesCount > 0 && (
                                            <SubtleBadge pill bg="danger" className="ms-1">{unreadNotesCount}</SubtleBadge>
                                        )}
                                    </Nav.Link>
                                </Nav.Item>
                                   {isJobCompleted && (
                                        <Nav.Item>
                                            <Nav.Link 
                                                eventKey="review" 
                                                className="cursor-pointer outline-none"
                                            >
                                                {t("review")}
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                    {isJobCompleted && (
                                        <Nav.Item>
                                            <Nav.Link 
                                                eventKey="complaint" 
                                                className="cursor-pointer outline-none"
                                            >
                                                {t("raiseAComplaint") || "Raise a complaint"}
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                </Nav>
                                {activeSection === "overview" && selectedTask?.ChecklistUrl && (
                                    <Row className="mb-3">
                                        <Col xs={1}><FontAwesomeIcon icon={faClipboardList} className="mt-1 text-success" /></Col>
                                        <Col><a href={selectedTask.ChecklistUrl} target="_blank" rel="noopener noreferrer" className="fw-semi-bold">{t("viewChecklist")}</a></Col>
                                    </Row>
                                )}

                                {activeSection === "overview" && (
                                <>
                                <div className="fw-medium font-size-15 mb-1">
                                    <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                                    {t("workProgram")}
                                </div>
                                <div className="work-program-card">
                                <div
                                    className="d-flex align-items-center justify-content-between pointer"
                                    onClick={() => setIsWorkflowOpen(prev => !prev)}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="work-program-icon-box d-flex align-items-center justify-content-center flex-shrink-0">
                                            <FontAwesomeIcon icon={faClipboardList} />
                                        </div>
                                        <div>
                                            <div className="fw-bold work-program-title mb-0">{t("tasks")}</div>
                                            <div className="text-muted work-program-subtitle">
                                                {completedStepsCount} {t("of")} {totalStepsCount} {t("completed")}
                                            </div>
                                        </div>
                                    </div>
                                    <FontAwesomeIcon
                                        icon={isWorkflowOpen ? faChevronUp : faChevronDown}
                                        className="text-secondary work-program-toggle-icon flex-shrink-0"
                                    />
                                </div>

                                {isWorkflowOpen && (
                                    stepsLoading ? (
                                        <div className="text-center py-3">
                                            <Spinner animation="border" size="sm" />
                                        </div>
                                    ) : (
                                    <div className="mt-3">
                                        {[...topSubtasks, ...middleSubtasks, ...bottomSubtasks].map((step, index, arr) => {
                                            const stepId = step.SubtaskId || step.ProposalSubtaskId || `step-${index}`;
                                            const isJobStarted = selectedTask?.Status?.toLowerCase() === "inprogress";
                                            const isJobEnded = selectedTask?.Status?.trim().toLowerCase() === "job complete";

                                            const hasActualStart =
                                                !!selectedTask?.ActualStartTime || Number(selectedTask?.Status) === 1;
                                            const hasActualEnd = !!selectedTask?.ActualEndTime;

                                            const isCompleted =
                                                hasActualEnd
                                                    ? true // All steps green
                                                    : hasActualStart && index < 2 // First two steps green
                                                        ? true
                                                        : false;

                                            const isLast = index === arr.length - 1;

                                            let stepTime = null;
                                            let stepTimeLabel = null;   // full date label when days differ
                                            let diffBadge = null;       // late/early badge for first and last step

                                            const actualStartDate = selectedTask?.ActualStartDate;
                                            const actualEndDate = selectedTask?.ActualEndDate;

                                            const actualStartTime = selectedTask?.ActualStartTime
                                                ? moment.utc(selectedTask.ActualStartTime).format("HH:mm:ss")
                                                : null;

                                            const actualEndTime = selectedTask?.ActualEndTime
                                                ? moment.utc(selectedTask.ActualEndTime).format("HH:mm:ss")
                                                : null;

                                            const actualStart = (actualStartDate && actualStartTime)
                                                ? moment.utc(`${actualStartDate}T${actualStartTime}`).local()
                                                : null;

                                            const actualEnd = (actualEndDate && actualEndTime)
                                                ? moment.utc(`${actualEndDate}T${actualEndTime}`).local()
                                                : actualStart
                                                    ? actualStart.clone().add(1, "hour")
                                                    : null;

                                            // ── Scheduled vs actual diff for first and last step ──────────
                                            const scheduledStart = selectedTask?.StartTime
                                                ? moment.utc(selectedTask.StartTime).local()
                                                : null;

                                            const scheduledEnd = selectedTask?.EndTime
                                                ? moment.utc(selectedTask.EndTime).local()
                                                : null;

                                            const buildDiffBadge = (diffMin, calendarDayDiff = null) => {
                                                const style = { fontSize: "10px" };

                                                if (calendarDayDiff !== null && Math.abs(calendarDayDiff) >= 1) {
                                                    const absDays = Math.abs(calendarDayDiff);
                                                    return calendarDayDiff < 0
                                                        ? <span className="badge bg-info text-white ms-1" style={style}>
                                                            {absDays} {absDays === 1 ? t("dayEarly") : t("daysEarly")}
                                                        </span>
                                                        : <span className="badge bg-warning text-white ms-1" style={style}>
                                                            {absDays} {absDays === 1 ? t("dayLate") : t("daysLate")}
                                                        </span>;
                                                }

                                                const absMins = Math.abs(diffMin);
                                                const absHours = Math.floor(absMins / 60);

                                                if (absMins <= 2) {
                                                    return <span className="badge bg-success text-white ms-1" style={style}>{t("onTime")}</span>;
                                                }
                                                if (absHours >= 1) {
                                                    return diffMin < 0
                                                        ? <span className="badge bg-info text-white ms-1" style={style}>{absHours}hr {t("early")}</span>
                                                        : <span className="badge bg-warning text-white ms-1" style={style}>{absHours}hr {t("late")}</span>;
                                                }
                                                return diffMin < 0
                                                    ? <span className="badge bg-info text-white ms-1" style={style}>{absMins} {t("minEarly")}</span>
                                                    : <span className="badge bg-warning text-white ms-1" style={style}>{absMins} {t("minLate")}</span>;
                                            };

                                            if (index === 0 && actualStart && selectedTask?.StartTime && selectedTask?.WeekStartDate) {
                                                const scheduledTimeOnly = moment.utc(selectedTask.StartTime).local().format("HH:mm");

                                                const scheduledStartCorrected = moment(
                                                    `${moment(selectedTask.WeekStartDate).format("YYYY-MM-DD")}T${scheduledTimeOnly}`
                                                );

                                                const diffMin = actualStart.diff(scheduledStartCorrected, "minutes");

                                                const calendarDayDiff = moment(selectedTask.ActualStartDate)
                                                    .diff(moment(selectedTask.WeekStartDate), "days");

                                                stepTimeLabel = Math.abs(calendarDayDiff) >= 1
                                                    ? actualStart.format("DD-MM-YYYY · HH:mm")
                                                    : null;

                                                diffBadge = buildDiffBadge(diffMin, calendarDayDiff);
                                            }

                                            if (isLast && actualEnd && selectedTask?.EndTime && selectedTask?.WeekStartDate) {

                                                const scheduledTimeOnly = moment.utc(selectedTask.EndTime).local().format("HH:mm");

                                                const scheduledEndCorrected = moment(
                                                    `${moment(selectedTask.WeekStartDate).format("YYYY-MM-DD")}T${scheduledTimeOnly}`
                                                );

                                                const diffMin = actualEnd.diff(scheduledEndCorrected, "minutes");

                                                const calendarDayDiff = moment(selectedTask.ActualEndDate)
                                                    .diff(moment(selectedTask.WeekStartDate), "days");

                                                stepTimeLabel = Math.abs(calendarDayDiff) >= 1
                                                    ? actualEnd.format("DD-MM-YYYY · HH:mm")
                                                    : null;
                                            }

                                            if (actualStart) {
                                                const totalSteps = arr.length;

                                                const computedEnd = actualEnd || actualStart.clone().add(1, "hour");

                                                const endBoundary = computedEnd.isSame(actualStart, "day")
                                                    ? computedEnd
                                                    : actualStart.clone().endOf("day");

                                                const totalDurationMinutes = Math.max(1, endBoundary.diff(actualStart, "minutes"));
                                                const interval = Math.floor(totalDurationMinutes / (totalSteps - 1));

                                                if (isLast) {
                                                    stepTime = endBoundary.format("HH:mm");
                                                } else {
                                                    const stepMoment = actualStart.clone().add(interval * index, "minutes");
                                                    stepTime = stepMoment.format("HH:mm");
                                                }
                                            }

                                            const isMiddleSubtask = index >= topSubtasks.length && index < topSubtasks.length + middleSubtasks.length;

                                            const isDone = isMiddleSubtask
                                                ? !!stepCompletions[stepId]
                                                : index < topSubtasks.length
                                                    ? hasActualStart
                                                    : hasActualEnd || !!stepCompletions[stepId];
                                            const canToggle = (userRole === "Employee" || isEmployeeView) &&
                                                (Number(selectedTask?.Status) === 1 || isOverdueTask) &&
                                                !isDone && !isJobCompleted;

                                            return (
                                                <div
                                                    key={stepId}
                                                    className={classNames("work-step-row d-flex align-items-center justify-content-between", {
                                                        "mb-2": !isLast,
                                                    })}
                                                >
                                                    <div className="d-flex align-items-center gap-2 min-w-0">
                                                        {stepSaving[stepId] ? (
                                                            <Spinner animation="border" size="sm" className="flex-shrink-0" />
                                                        ) : (
                                                            <div
                                                                className={classNames("work-step-circle flex-shrink-0", {
                                                                    "work-step-circle-done": isDone,
                                                                    "work-step-circle-complete": isDone && isAllStepsComplete,
                                                                    "work-step-circle-clickable": canToggle,
                                                                })}
                                                                title={
                                                                    isJobCompleted && !isDone
                                                                        ? t("jobAlreadyCompleted")
                                                                        : isDone
                                                                            ? t("completedTaskCannotBeMarkedIncomplete")
                                                                            : canToggle
                                                                                ? t("markComplete")
                                                                                : undefined
                                                                }
                                                                onClick={() => {
                                                                    if (canToggle) handleStepToggle(stepId);
                                                                }}
                                                            >
                                                                {isDone && <FontAwesomeIcon icon={faCheck} />}
                                                            </div>
                                                        )}
                                                        <span className={classNames("work-step-label text-truncate", {
                                                            "work-step-label-done": isDone,
                                                        })}>
                                                            {step.Subtask || step.SubtaskName || t("unnamedStep")}
                                                        </span>
                                                        {diffBadge}
                                                    </div>

                                                    {isDone && (
                                                            <span className="work-step-done-label text-muted flex-shrink-0">
                                                                <FontAwesomeIcon icon={faClock} className="me-1" />
                                                                {stepCompletions[stepId]?.CompletedAt
                                                                    ? moment.utc(stepCompletions[stepId].CompletedAt).local().format("DD-MM-YY · HH:mm")
                                                                    : (index < 2 && hasActualStart)
                                                                        ? moment.utc(selectedTask.ActualStartTime).local().format("DD-MM-YY · HH:mm")
                                                                        : t("done")}
                                                            </span>
                                                        )}
                                                </div>
                                            );

                                        })}
                                    </div>
                                    )
                                )}
                                </div>
                                </>
                                )}

                                {activeSection === "overview" && (
                                    (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER) ||
                                    (userRole === USER_ROLES.EMPLOYEE && selectedTask?.Status === 2) ||
                                    userRole === USER_ROLES.CLIENT
                                ) && selectedTask?.ClientSignature && (
                                        <div className="mt-4">
                                            <div className="fw-medium font-size-15 mb-1">
                                                <FontAwesomeIcon icon={faSignature} className="me-2" />
                                                {t("clientSignature")}
                                            </div>

                                            <div className="detail-section-card d-flex align-items-center gap-3">
                                                <img
                                                    src={`data:image/png;base64,${selectedTask.ClientSignature}`}
                                                    alt={t("clientSignature")}
                                                    className="signature flex-shrink-0"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {activeSection === "overview" && (
                                        (userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.APP_OWNER) ||
                                        (userRole === USER_ROLES.EMPLOYEE && Number(selectedTask?.Status) === 2)
                                    ) && !!selectedTask?.ActualEndTime && (
                                            <div className="mt-4">
                                                <div className="fw-medium font-size-15 mb-1">
                                                    <FontAwesomeIcon icon={faClipboardList} className="me-2" />
                                                    {t("notes")}
                                                </div>

                                                <div className="detail-section-card">
                                                    <p className="mb-0 detail-section-text" style={{ whiteSpace: "pre-wrap" }}>
                                                        {selectedTask?.Notes || t("noNotesAvailable")}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                
                                    {activeSection === "discussion" && (
                                        <div className="mt-2">
                                            {notesLoading ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : taskNotes.length === 0 ? (
                                                <div className="discussion-empty-state">
                                                    <p className="text-muted mb-0 data-font-size">{t("startDiscussion")}</p>
                                                </div>
                                            ) : (
                                                [...taskNotes]
                                                    .sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)))
                                                    .map((note, idx, arr) => {
                                                        const authorName = note.AuthorName || t("unknownUser");
                                                        const initials = authorName
                                                            .split(" ")
                                                            .map((w) => w[0])
                                                            .join("")
                                                            .slice(0, 2)
                                                            .toUpperCase();
                                                        const avatarColorClass = getDiscussionAvatarColorClass(authorName);

                                                        return (
                                                            <div
                                                                key={note.TaskDiscussionId}
                                                                className={classNames("d-flex gap-2 py-3")}
                                                            >
                                                                <div className="avatar flex-shrink-0 note-avatar">
                                                                    <div className={`avatar-name rounded-circle ${avatarColorClass} d-flex align-items-center justify-content-center w-100 h-100 fs-10`}>
                                                                        <span className="fw-bold">{initials}</span>
                                                                    </div>
                                                                </div>

                                                                <div className="flex-grow-1 min-w-0">
                                                                    <div className="d-flex justify-content-between align-items-start">
                                                                        <span className="data-font-size">
                                                                            <span className="fw-bold">{authorName}</span>
                                                                            {note.AuthorRole && (
                                                                               <span className={`badge rounded-pill ms-2 fw-medium ${getRoleBadgeStyle(note.AuthorRole)}`} style={{ fontSize: "10px" }}>
                                                                                    {t(normalizeRole(note.AuthorRole))}
                                                                                </span>
                                                                            )}
                                                                        </span>
                                                                        <span className="text-muted flex-shrink-0 ms-2 fs-10">
                                                                            {moment.utc(note.createdAt).local().format("DD-MM · HH:mm")}
                                                                        </span>
                                                                    </div>
                                                                    <p className="mb-0 mt-1 data-font-size" style={{ whiteSpace: "pre-wrap" }}>
                                                                        {note.NoteText}
                                                                    </p>
                                                                        {note.ImageUrls?.length > 0 && (
                                                                            <div className="d-flex flex-wrap gap-2 mt-2">
                                                                                {note.ImageUrls.map((url, idx) => (
                                                                                    <img
                                                                                        key={idx}
                                                                                        src={url}
                                                                                        alt="attachment"
                                                                                        className="img-thumbnail task-photo-thumbnail pointer"
                                                                                        onClick={() => openGalleryPreview(note.ImageUrls, idx)}
                                                                                    />
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                            )}
                                        </div>
                                    )}

                                    {activeSection === "review" && isJobCompleted && (
                                    <div className="mt-2">
                                        <div className="fw-medium font-size-15 mb-1">
                                            <FontAwesomeIcon icon={faStar} className="me-2" />
                                            <span className="fw-semi-bold">{t("review")}</span>
                                        </div>
                                        
                                            {reviewLoading ? (
                                                <div className="text-center py-3">
                                                    <Spinner animation="border" size="sm" />
                                                </div>
                                            ) : existingReview && !isWritingReview ? (
                                                <div className="detail-section-card">
                                                    {/* Row 1: Rating label left, date right */}
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <h6 className="work-program-title mb-0">{t("rating")}</h6>
                                                        <span className="text-muted fs-10 flex-shrink-0 ms-2">
                                                            {moment(existingReview.createdAt).format("DD-MM-YYYY · HH:mm")}
                                                        </span>
                                                    </div>

                                                    {/* Row 2: stars left, rating value right beside stars */}
                                                    <div className="mb-2">
                                                        <div className="d-flex gap-1 justify-content-start my-3">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <i
                                                                    key={star}
                                                                    className={`fas fa-star review-star-icon-sm ${
                                                                        star <= existingReview.Rating ? "text-warning" : "text-muted opacity-50"
                                                                    }`}
                                                                />
                                                            ))}
                                                        </div>
                                                        <span className="fw-semi-bold text-primary data-font-size d-block text-start">
                                                            {getRatingLabel(existingReview.Rating)}
                                                        </span>
                                                    </div>

                                                    {/* Feedback */}
                                                    <>
                                                    <hr className="task-section-divider my-3" />
                                                    <h6 className="work-program-title mb-2">{t("feedback")}</h6>
                                                    {existingReview.Comment && (
                                                        <p className="my-3 detail-section-text">
                                                            {existingReview.Comment}
                                                        </p>
                                                    )}
                                                    </>

                                                    {/* Photos gallery */}
                                                    {existingReview.ReviewImageUrls?.length > 0 && (
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {existingReview.ReviewImageUrls.map((url, idx) => (
                                                                <img
                                                                    key={idx}
                                                                    src={url}
                                                                    alt={`review-${idx}`}
                                                                    className="img-thumbnail task-photo-thumbnail pointer"
                                                                    onClick={() => openGalleryPreview(existingReview.ReviewImageUrls, idx)}
                                                                />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : userRole !== USER_ROLES.CLIENT ? (
                                                <div className="detail-section-card text-center py-3">
                                                    <div className="avatar-name rounded-circle d-inline-flex align-items-center justify-content-center review-icon-wrapper mb-2">
                                                        <FontAwesomeIcon icon={faStar} />
                                                    </div>
                                                    <p className="text-muted mb-0 data-font-size">{t("noReviewYet")}</p>
                                                </div>
                                            ) : !isWritingReview ? (
                                                <div className="detail-section-card text-center py-3">
                                                    <div className="avatar-name rounded-circle d-inline-flex align-items-center justify-content-center review-icon-wrapper mb-2">
                                                        <FontAwesomeIcon icon={faStar} />
                                                    </div>
                                                    <h6 className="fw-semi-bold mb-3 fs-9">{t("shareYourExperience")}</h6>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        className="px-4 review-cta-btn"
                                                        onClick={() => setIsWritingReview(true)}
                                                    >
                                                        <FontAwesomeIcon icon={faPen} className="me-2" />
                                                        {t("writeAReview")}
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="detail-section-card">
                                                    {/* Rating Section */}
                                                    <div className="text-start mb-3">
                                                        <h6 className="work-program-title mb-3">{t("rating")}</h6>
                                                        <div className="d-flex justify-content-start gap-3" role="radiogroup">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <button
                                                                    key={star}
                                                                    type="button"
                                                                    className="review-star-btn btn btn-link p-0 border-0"
                                                                    role="radio"
                                                                    aria-checked={star === reviewRating}
                                                                    onMouseEnter={() => setHoveredStar(star)}
                                                                    onMouseLeave={() => setHoveredStar(0)}
                                                                    onClick={() => setReviewRating(star)}
                                                                >
                                                                    <i
                                                                        className={`fas fa-star review-star-icon ${
                                                                            star <= (hoveredStar || reviewRating) ? "text-warning" : "text-muted opacity-50"
                                                                        }`}
                                                                        aria-hidden="true"
                                                                    />
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {reviewRating > 0 && (
                                                            <span className="fw-semi-bold text-primary data-font-size d-block mt-2">
                                                                {getRatingLabel(reviewRating)}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <hr className="task-section-divider my-3" />
                                                    {/* Feedback Comments */}
                                                    <h6 className="work-program-title mb-2">{t("feedback")}</h6>
                                                        <textarea
                                                            className="form-control data-font-size review-textarea mb-3"
                                                            rows="4"
                                                            placeholder={t("reviewPlaceholder")}
                                                            value={reviewComment}
                                                            onChange={(e) => setReviewComment(e.target.value)}
                                                        />

                                                        {/* Photo Upload */}
                                                        <div className="mb-3">
                                                            <div className="position-relative">
                                                                <button
                                                                    type="button"
                                                                    className="feedback-photo-pill-btn"
                                                                    onClick={() => {
                                                                        if (isMobileDevice) {
                                                                            setShowFeedbackAttachMenu(prev => !prev);
                                                                        } else {
                                                                            const input = document.createElement('input');
                                                                            input.type = 'file';
                                                                            input.accept = 'image/*';
                                                                            input.multiple = true;
                                                                            input.onchange = (e) => {
                                                                                if (e.target.files) {
                                                                                    Array.from(e.target.files).forEach(file => handleFeedbackImageAdd(file));
                                                                                }
                                                                            };
                                                                            input.click();
                                                                        }
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faImage} className="me-2" />
                                                                    {t("addPhotos")}
                                                                </button>

                                                                {isMobileDevice && showFeedbackAttachMenu && (
                                                                    <>
                                                                        <div
                                                                            className="position-fixed top-0 start-0 w-100 h-100"
                                                                            style={{ zIndex: 1049 }}
                                                                            onClick={() => setShowFeedbackAttachMenu(false)}
                                                                        />
                                                                        <div className="chat-attach-menu feedback-attach-menu">
                                                                            <button
                                                                                type="button"
                                                                                className="chat-attach-menu-item"
                                                                                onClick={() => {
                                                                                    setShowFeedbackAttachMenu(false);
                                                                                    const input = document.createElement('input');
                                                                                    input.type = 'file';
                                                                                    input.accept = 'image/*';
                                                                                    input.capture = 'environment';
                                                                                    input.onchange = (e) => {
                                                                                        if (e.target.files?.[0]) {
                                                                                            handleFeedbackImageAdd(e.target.files[0]);
                                                                                        }
                                                                                    };
                                                                                    input.click();
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon icon={faCamera} className="me-2" />
                                                                                {t("takePhoto")}
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="chat-attach-menu-item"
                                                                                onClick={() => {
                                                                                    setShowFeedbackAttachMenu(false);
                                                                                    const input = document.createElement('input');
                                                                                    input.type = 'file';
                                                                                    input.accept = 'image/*';
                                                                                    input.multiple = true;
                                                                                    input.onchange = (e) => {
                                                                                        if (e.target.files) {
                                                                                            Array.from(e.target.files).forEach(file => handleFeedbackImageAdd(file));
                                                                                        }
                                                                                    };
                                                                                    input.click();
                                                                                }}
                                                                            >
                                                                                <FontAwesomeIcon icon={faImage} className="me-2" />
                                                                                {t("uploadFromGallery")}
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>

                                                            {feedbackImages.length > 0 && (
                                                                <div className="d-flex flex-wrap gap-2 mt-3">
                                                                    {feedbackImages.map((photo, idx) => (
                                                                        <div key={idx} className="position-relative photo-thumbnail-wrapper">
                                                                            <img
                                                                                src={feedbackPhotoUrls[idx]}
                                                                                alt={`Feedback photo ${idx + 1}`}
                                                                                className="img-thumbnail task-photo-thumbnail pointer"
                                                                                onClick={() => openGalleryPreview(feedbackPhotoUrls, idx)}
                                                                            />
                                                                            <span
                                                                                className="photo-remove-btn position-absolute top-0 end-0 cursor-pointer text-danger lh-1 z-3"
                                                                                onClick={() => handleFeedbackImageRemove(idx)}
                                                                                title={t("remove")}
                                                                            >
                                                                                <i className="fas fa-times-circle" />
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Action Buttons */}
                                                        <div className="d-flex gap-2 justify-content-end">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="review-action-btn"
                                                            onClick={() => {
                                                                setIsWritingReview(false);
                                                                setReviewRating(0);
                                                                setReviewComment("");
                                                                setFeedbackImages([]);
                                                            }}
                                                        >
                                                            {t("cancel")}
                                                        </Button>
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            className="review-action-btn"
                                                            disabled={reviewRating === 0 || isSubmittingReview}
                                                            onClick={handleReviewSubmit}
                                                        >
                                                            {isSubmittingReview ? t("saving") : t("submit")}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {activeSection === "complaint" && isJobCompleted && (
                                        <div className="mt-2">
                                            {complaintLoading ? (
                                                <div className="text-center py-3">
                                                    <Spinner animation="border" size="sm" />
                                                </div>
                                            ) : existingComplaint ? (
                                                <div className="detail-section-card">
                                                    {/* Header: Reason & Date */}
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <h6 className="work-program-title mb-0">{t("reason") || "Reason"}</h6>
                                                        <span className="text-muted fs-10 flex-shrink-0 ms-2">
                                                            {moment(existingComplaint.createdAt).format("DD-MM-YYYY · HH:mm")}
                                                        </span>
                                                    </div>

                                                        <p className="fw-semi-bold data-font-size data-font-colour mb-2">
                                                            {t(existingComplaint.ComplaintReason) || existingComplaint.ComplaintReason}
                                                        </p>

                                                    {/* Details */}
                                                    {existingComplaint.Comment && (
                                                        <>
                                                            <hr className="task-section-divider my-3" />
                                                            <h6 className="work-program-title mb-2">{t("details") || "Details"}</h6>
                                                            <p className="my-2 detail-section-text">
                                                                {existingComplaint.Comment}
                                                            </p>
                                                        </>
                                                    )}

                                                    {/* Photos */}
                                                    {existingComplaint.ComplaintImageUrls?.length > 0 && (
                                                        <>
                                                            <hr className="task-section-divider my-3" />
                                                            <h6 className="work-program-title mb-2">{t("photos") || "Photos"}</h6>
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {existingComplaint.ComplaintImageUrls.map((url, idx) => (
                                                                    <img
                                                                        key={idx}
                                                                        src={url}
                                                                        alt={`complaint-${idx}`}
                                                                        className="img-thumbnail task-photo-thumbnail pointer"
                                                                        onClick={() => openGalleryPreview(existingComplaint.ComplaintImageUrls, idx)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ) : userRole !== USER_ROLES.CLIENT ? (
                                                <div className="detail-section-card text-center py-3">
                                                    <p className="text-muted mb-0 data-font-size">
                                                        {t("noComplaintReported") || "No complaint reported for this task."}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="detail-section-card">
                                                    {/* Reason Dropdown */}
                                                    <div className="mb-3">
                                                        <h6 className="work-program-title mb-2">
                                                            {t("reason") || "Reason"} <span className="text-danger">*</span>
                                                        </h6>
                                                        <Select
                                                            options={COMPLAINT_REASONS.map((item) => ({
                                                                value: item.key || item,
                                                                label: typeof item === 'object' ? t(item.key) : item,
                                                            }))}
                                                            value={
                                                                selectedComplaintReason
                                                                    ? {
                                                                        value: selectedComplaintReason,
                                                                        label: (() => {
                                                                            const found = COMPLAINT_REASONS.find(
                                                                                (item) => (item.key || item) === selectedComplaintReason
                                                                            );
                                                                            return found
                                                                                ? (typeof found === 'object' ? t(found.key) : found)
                                                                                : selectedComplaintReason;
                                                                        })(),
                                                                    }
                                                                    : null
                                                            }
                                                            onChange={(selectedOption) => {
                                                                setSelectedComplaintReason(selectedOption?.value || "");
                                                                setComplaintReasonError(false);
                                                            }}
                                                            placeholder={t("selectAReason") || "Select a reason"}
                                                            isClearable
                                                            className="custom-select-container data-font-size"
                                                            classNamePrefix="custom-select"
                                                        />
                                                        {complaintReasonError && (
                                                            <div className="text-danger fs-10 mt-1">
                                                                {t("selectAReasonError") || "Select a reason."}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <hr className="task-section-divider my-3" />

                                                    {/* Details Textarea */}
                                                    <div className="mb-3">
                                                        <h6 className="work-program-title mb-2">{t("details") || "Details"}</h6>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={4}
                                                            className="data-font-size"
                                                            placeholder={t("detailsPlaceholder") || "Add any additional details."}
                                                            value={complaintComment}
                                                            onChange={(e) => setComplaintComment(e.target.value)}
                                                        />
                                                    </div>

                                                    {/* Photo Upload */}
                                                    <div className="mb-3">
                                                        <button
                                                            type="button"
                                                            className="feedback-photo-pill-btn"
                                                            onClick={() => {
                                                                if (!selectedComplaintReason) {
                                                                    setComplaintReasonError(true);
                                                                    toast.error(t("pleaseSelectReasonFirst") || "Please select a reason first", { position: "top-right" });
                                                                    return;
                                                                }
                                                                const input = document.createElement('input');
                                                                input.type = 'file';
                                                                input.accept = 'image/*';
                                                                input.multiple = true;
                                                                input.onchange = (e) => {
                                                                    if (e.target.files) {
                                                                        Array.from(e.target.files).forEach(file => handleComplaintImageAdd(file));
                                                                    }
                                                                };
                                                                input.click();
                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faImage} className="me-2" />
                                                            {t("addPhotos") || "Add Photos"}
                                                        </button>
                                                        {complaintImages.length > 0 && (
                                                            <div className="d-flex flex-wrap gap-2 mt-3">
                                                                {complaintImages.map((_, idx) => (
                                                                    <div key={idx} className="position-relative photo-thumbnail-wrapper">
                                                                        <img
                                                                            src={complaintPhotoUrls[idx]}
                                                                            alt={`Complaint photo ${idx + 1}`}
                                                                            className="img-thumbnail task-photo-thumbnail pointer"
                                                                            onClick={() => openGalleryPreview(complaintPhotoUrls, idx)}
                                                                        />
                                                                        <span
                                                                            className="photo-remove-btn position-absolute top-0 end-0 cursor-pointer text-danger lh-1 z-3"
                                                                            onClick={() => handleComplaintImageRemove(idx)}
                                                                            title={t("remove") || "Remove"}
                                                                        >
                                                                            <i className="fas fa-times-circle" />
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Action Buttons */}
                                                    <div className="d-flex gap-2 justify-content-end mt-4">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="review-action-btn"
                                                            onClick={() => {
                                                                setSelectedComplaintReason("");
                                                                setComplaintComment("");
                                                                setComplaintImages([]);
                                                                setComplaintReasonError(false);
                                                                setActiveSection("overview");
                                                            }}
                                                        >
                                                            {t("cancel") || "Cancel"}
                                                        </Button>
                                                        <Button
                                                            variant="primary"
                                                            size="sm"
                                                            className="review-action-btn"
                                                            disabled={!selectedComplaintReason || isSubmittingComplaint}
                                                            onClick={handleComplaintSubmit}
                                                        >
                                                            {isSubmittingComplaint ? (t("saving") || "Saving") : (t("submit") || "Submit")}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeSection === "overview" && !isEditingJob && (
                                        <div className="mt-4">
                                            <div className="fw-medium font-size-15 mb-1">
                                                <FontAwesomeIcon icon={faImage} className="me-2" />
                                                {t("taskPhotos")}
                                            </div>

                                            <div className="detail-section-card">
                                                <div className="d-flex flex-wrap gap-2">
                                                    {imagesLoading ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : completionImages.length === 0 ? (
                                                        <p className="text-muted mb-0 data-font-size">{t("noPhotosAvailable")}</p>
                                                    ) : (
                                                        completionImages.map((img, index) => (
                                                            <img
                                                                key={index}
                                                                src={`data:image/${img.ImageExtension};base64,${img.Image}`}
                                                                alt={`${t("taskPhoto")} ${index + 1}`}
                                                                className="img-thumbnail task-photo-thumbnail pointer"
                                                                onClick={() => setPreviewIndex(index)}
                                                            />
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {activeSection === "overview" && (
                                <Col xs={12} lg="auto" className="fw-semi-bold ls mb-1 fs-9 d-flex align-items-center mt-4">
                                    {activeSection === "overview" && (userRole === USER_ROLES.EMPLOYEE || isEmployeeView)  && context !== "calendar" && (
                                        <>
                                            {/* ── Start Job ── shown when job hasn't started yet */}
                                            {Number(selectedTask?.Status) !== 1 && !selectedTask?.ActualEndTime && !isOverdueTask && (
                                                <div className="d-flex align-items-center mb-3">
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        className="shadow-sm rounded-pill px-3"
                                                        onClick={async (e) => {
                                                            await onStartJob(e, selectedTask);
                                                            fetchStepCompletions(selectedTask?.OrderDetailId);
                                                        }}
                                                    >
                                                        {t("startJob")}
                                                    </Button>
                                                </div>
                                            )}

                                            {/* ── End Job ── shown when job has started but not ended */}
                                            {((selectedTask?.Status === "1" || selectedTask?.Status === 1) || isOverdueTask) &&
                                                !selectedTask?.ActualEndTime && (
                                                    !isEditingJob ? (
                                                        <div className="d-flex align-items-center mb-3">
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                className="shadow-sm rounded-pill px-3"
                                                                onClick={() => setIsEditingJob(true)}
                                                            >
                                                                {t("endJob")}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="task-datetime-form border rounded shadow-sm p-3 bg-light">
                                                            {/* Section Header */}
                                                            <h6 className="fw-bold border-bottom pb-2 mb-3">{t("jobTime")}</h6>

                                                            <Row className="gy-3">
                                                                {/* Start Date for Overdue Task */}
                                                                {isOverdueTask && (
                                                                    <Col xs={12} md={6}>
                                                                        <div className="d-flex flex-row align-items-center">
                                                                            <label className="fw-semibold me-2 complete-job-date-time" >
                                                                                {t("startDate")}
                                                                            </label>
                                                                            <Controller
                                                                                control={control}
                                                                                name="editStartDate"
                                                                                render={({ field }) => (
                                                                                    <FixedDatePicker
                                                                                        field={field}
                                                                                        error={errors.editStartDate}
                                                                                        maxDate={moment().subtract(1, 'day').toDate()}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </div>
                                                                        {errors.editStartDate && (
                                                                            <div className="invalid-feedback d-block">{errors.editStartDate.message}</div>
                                                                        )}
                                                                    </Col>
                                                                )}

                                                                {/* Start Time */}
                                                                {(isOverdueTask || !isOverdueTask) && (
                                                                    <Col xs={12} md={6}>
                                                                        <div className="d-flex flex-row align-items-center">
                                                                            <label className="fw-semibold me-2 complete-job-date-time">
                                                                                {t("startTime")}
                                                                            </label>
                                                                            <Controller
                                                                                control={control}
                                                                                name={isOverdueTask ? "editStartTime" : "actualStartTime"}
                                                                                render={({ field }) => (
                                                                                    <FixedTimePicker
                                                                                        field={field}
                                                                                        error={isOverdueTask ? errors.editStartTime : errors.actualStartTime}
                                                                                        customInput={<TimeInput className="form-control form-control-sm complete-job-date-time" />}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </div>
                                                                        {(isOverdueTask && errors.editStartTime) && (
                                                                            <div className="invalid-feedback d-block">{errors.editStartTime.message}</div>
                                                                        )}
                                                                        {(!isOverdueTask && errors.actualStartTime) && (
                                                                            <div className="invalid-feedback d-block">{errors.actualStartTime.message}</div>
                                                                        )}
                                                                    </Col>
                                                                )}

                                                                {/* End Date for Overdue Task */}
                                                                {isOverdueTask && (
                                                                    <Col xs={12} md={6}>
                                                                        <div className="d-flex flex-row align-items-center">
                                                                            <label className="fw-semibold me-2 complete-job-date-time">
                                                                                {t("endDate")}
                                                                            </label>
                                                                            <Controller
                                                                                control={control}
                                                                                name="editEndDate"
                                                                                render={({ field }) => (
                                                                                    <FixedDatePicker
                                                                                        field={field}
                                                                                        error={errors.editEndDate}
                                                                                        maxDate={moment().subtract(1, 'day').toDate()}
                                                                                    />
                                                                                )}
                                                                            />
                                                                        </div>
                                                                        {errors.editEndDate && (
                                                                            <div className="invalid-feedback d-block">{errors.editEndDate.message}</div>
                                                                        )}
                                                                    </Col>
                                                                )}

                                                                {/* End Time for both overdue and normal tasks */}
                                                                <Col xs={12} md={6}>
                                                                    <div className="d-flex flex-row align-items-center">
                                                                        <label className="fw-semibold me-2 complete-job-date-time">
                                                                            {t("endTime")}
                                                                        </label>
                                                                        <Controller
                                                                            control={control}
                                                                            name={isOverdueTask ? "editEndTime" : "actualEndTime"}
                                                                            render={({ field }) => (
                                                                                <FixedTimePicker
                                                                                    field={field}
                                                                                    error={isOverdueTask ? errors.editEndTime : errors.actualEndTime}
                                                                                    customInput={<TimeInput className="form-control form-control-sm complete-job-date-time" />}
                                                                                />
                                                                            )}
                                                                        />
                                                                    </div>
                                                                    {(isOverdueTask && errors.editEndTime) && (
                                                                        <div className="invalid-feedback d-block">{errors.editEndTime.message}</div>
                                                                    )}
                                                                    {(!isOverdueTask && errors.actualEndTime) && (
                                                                        <div className="invalid-feedback d-block">{errors.actualEndTime.message}</div>
                                                                    )}
                                                                </Col>
                                                            </Row>

                                                            <Col xs={12} md={12} className="d-flex align-items-center mt-3 mb-3">
                                                                <label className="fw-semibold me-2 mb-0 complete-job-date-time">
                                                                    {t("status")}
                                                                </label>
                                                                <Controller
                                                                    name="status"
                                                                    id="status-select"
                                                                    control={control}
                                                                    defaultValue="2"
                                                                    rules={{
                                                                        required: t("statusRequired"),
                                                                        validate: (value) => value !== "" || t("statusRequired"),
                                                                    }}
                                                                    render={({ field }) => (
                                                                        <Form.Select
                                                                            {...field}
                                                                            value={field.value || "2"} //  Ensure fallback if value is empty
                                                                            size="sm"
                                                                            className={`${errors.status ? "is-invalid" : ""}`}
                                                                            style={{ width: "200px" }}
                                                                        >
                                                                            <option value="2">{t("jobComplete")}</option>
                                                                            <option value="3">{t("unableToAccessLocation")}</option>
                                                                            <option value="4">{t("customerNotAvailable")}</option>
                                                                            <option value="5">{t("equipmentIssue")}</option>
                                                                            <option value="6">{t("cancelledByCustomer")}</option>
                                                                            <option value="7">{t("incomplete")}</option>
                                                                        </Form.Select>
                                                                    )}
                                                                />

                                                                {errors.status && (
                                                                    <div className="invalid-feedback d-block">{errors.status.message}</div>
                                                                )}
                                                            </Col>

                                                            {/* Total Hours */}
                                                            <Col xs={12} md={6} className="d-flex align-items-center mb-3">
                                                                <label className="fw-semibold me-2 mb-0 text-nowrap d-inline-block">
                                                                    {t("totalHours")}:{" "}
                                                                    {isOverdueTask ? (
                                                                        (() => {
                                                                            const start = watch("editStartTime");
                                                                            const end = watch("editEndTime");
                                                                            if (start && end) {
                                                                                const diffMinutes = Math.max(0, moment(end).diff(moment(start), "minutes") - breakMinutes);
                                                                                const hours = Math.floor(diffMinutes / 60);
                                                                                const minutes = diffMinutes % 60;
                                                                                return `${hours}${t("hourUnit")} ${minutes}${t("minutes")}`;
                                                                            }
                                                                            return `0${t("hourUnit")} 0${t("minutes")}`;
                                                                        })()
                                                                    ) : (
                                                                        formatTotalHours(
                                                                            moment().format("YYYY-MM-DD"),
                                                                            watch("actualStartTime"),
                                                                            watch("actualEndTime"),
                                                                            breakMinutes,
                                                                            t
                                                                        ).formatted
                                                                    )}
                                                                </label>
                                                            </Col>

                                                            {/*pause section*/}
                                                            {proposalFlags.pauseRequired && (
                                                                <Col xs={12} md={12} className="mb-3">
                                                                    <div className="d-flex align-items-center">
                                                                        {/* Left label */}
                                                                        <label className="fw-semibold me-3 mb-0">
                                                                            {t("break")}:
                                                                        </label>

                                                                        {/* Small toggle button group with validation error on click */}
                                                                        <div className="btn-group" role="group" aria-label="Break toggle button group">
                                                                            {[0, 15, 30, 45, 60].map((min) => {
                                                                                // calculate total job minutes from actual times
                                                                                let totalMinutes = 0;
                                                                                if (isOverdueTask) {
                                                                                    const start = watch("editStartTime");
                                                                                    const end = watch("editEndTime");
                                                                                    if (start && end) {
                                                                                        totalMinutes = Math.max(0, Math.round(moment(end).diff(moment(start), "minutes")));
                                                                                    }
                                                                                } else {
                                                                                    const { hours, minutes } = formatTotalHours(
                                                                                        moment().format("YYYY-MM-DD"),
                                                                                        watch("actualStartTime"),
                                                                                        watch("actualEndTime"),
                                                                                        0,
                                                                                        t
                                                                                    );
                                                                                    totalMinutes = hours * 60 + minutes;
                                                                                }

                                                                                const isInvalidBreak = min >= totalMinutes && min !== 0;

                                                                                return (
                                                                                    <React.Fragment key={min}>
                                                                                        <input
                                                                                            type="radio"
                                                                                            className="btn-check"
                                                                                            name="breakOptions"
                                                                                            id={`break-${min}`}
                                                                                            autoComplete="off"
                                                                                            checked={breakMinutes === min}
                                                                                            onChange={() => {
                                                                                                if (isInvalidBreak) {
                                                                                                    toast.error(t("breakValidation"), {
                                                                                                        position: "top-right",
                                                                                                        autoClose: 3000

                                                                                                    });
                                                                                                    return;
                                                                                                }
                                                                                                setBreakMinutes(min);
                                                                                            }}
                                                                                        />
                                                                                        <label
                                                                                            className="btn btn-outline-primary btn-sm  break-buttons"
                                                                                            htmlFor={`break-${min}`}
                                                                                        >
                                                                                            {min === 0 ? t("noBreak") : `${min} min`}
                                                                                        </label>
                                                                                    </React.Fragment>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            )}

                                                            {/* Signature Section */}
                                                           
                                                                <Col xs={12} md={12} className="mb-3 align-items-center mt-2">
                                                                    <label className="fw-semi-bold ls mb-2 fs-9">{t("clientSignature")}</label>
                                                                    <div
                                                                        className="sig-canvas-wrapper position-relative d-flex align-items-center justify-content-center sig-canvas-height"
                                                                        onClick={() => setShowSignatureModal(true)}
                                                                    >
                                                                        {tempClientSignature ? (
                                                                            <img
                                                                                src={`data:image/png;base64,${tempClientSignature}`}
                                                                                alt={t("clientSignature")}
                                                                                className="signature-preview-img"
                                                                            />
                                                                        ) : (
                                                                            <span className="text-muted">{t("clickToSign")}</span>
                                                                        )}
                                                                        {tempClientSignature && (
                                                                            <div
                                                                                className="position-absolute top-0 end-0 m-1"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setTempClientSignature(null);
                                                                                }}
                                                                                title={t("clear")}
                                                                            >
                                                                                <button className="btn btn-light rounded-circle p-0 shadow-sm me-1 sig-clear-btn">
                                                                                    <i className="fa fa-broom"></i>
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </Col>
                                                           

                                                            {/* Notes Section */}
                                                            <Col xs={12} md={12} className="mb-3 align-items-center mt-0">
                                                                <textarea
                                                                    className="form-control fw-sm me-2 mb-0"
                                                                    placeholder={t("notes")}
                                                                    rows="2"
                                                                    value={noteValue}
                                                                    onChange={(e) => setNoteValue(e.target.value)}
                                                                />
                                                            </Col>

                                                            {/* Photo Upload Section */}
                                                            <Col
                                                                xs={12}
                                                                md={12}
                                                                className="mb-3"
                                                                onDragOver={handleDragOver}
                                                                onDragLeave={handleDragLeave}
                                                                onDrop={handleDrop}
                                                            >
                                                                <label className="fw-semibold mb-3">{t("taskPhotos")}</label>

                                                                {/* Photo Gallery */}
                                                                {taskPhotos.length > 0 && (
                                                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                                                        {taskPhotos.map((photo, idx) => (
                                                                            <div key={idx} className="position-relative photo-thumbnail-wrapper">
                                                                                <img
                                                                                    src={taskPhotoUrls[idx]}
                                                                                    alt={`Task photo ${idx + 1}`}
                                                                                    className="img-thumbnail task-photo-thumbnail pointer"
                                                                                    onClick={() => setPreviewIndex(idx)}
                                                                                />
                                                                                <span
                                                                                    className="photo-remove-btn position-absolute top-0 end-0 cursor-pointer text-danger lh-1 z-3"
                                                                                    onClick={() => handlePhotoRemove(idx)}
                                                                                    title={t("remove")}
                                                                                >
                                                                                    <i className="fas fa-times-circle" />
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                {/* Upload Buttons */}
                                                                <div className="d-flex gap-2">
                                                                    {/* Will appear only on mobile and tablet */}
                                                                    {isMobileDevice && (
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            disabled={isPayloadFull}
                                                                            onClick={() => {
                                                                                const input = document.createElement('input');
                                                                                input.type = 'file';
                                                                                input.accept = 'image/*';
                                                                                input.capture = 'environment';
                                                                                input.onchange = (e) => {
                                                                                    if (e.target.files?.[0]) {
                                                                                        handlePhotoCapture(e.target.files[0]);
                                                                                    }
                                                                                };
                                                                                input.click();
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faCamera} className="me-1" />
                                                                            {t("takePhoto")}
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        disabled={isPayloadFull}
                                                                        onClick={() => {
                                                                            const input = document.createElement('input');
                                                                            input.type = 'file';
                                                                            input.accept = 'image/*';
                                                                            input.multiple = true;
                                                                            input.onchange = (e) => {
                                                                                if (e.target.files) {
                                                                                    Array.from(e.target.files).forEach(file => handlePhotoCapture(file));
                                                                                }
                                                                            };
                                                                            input.click();
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faImage} className="me-1" />
                                                                        {t("uploadFromGallery")}
                                                                    </Button>
                                                                </div>
                                                                {isPayloadFull && (
                                                                    <div className="mt-2 text-danger small">
                                                                        <i className="fas fa-info-circle me-1"></i>
                                                                        {t("maxPayloadReachedMessage", { maxSize: MAX_PAYLOAD_SIZE_MB })}
                                                                    </div>
                                                                )}
                                                            </Col>

                                                            {/* Actions */}
                                                            <div className="d-flex justify-content-end gap-2 border-top pt-3">
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    size="sm"
                                                                    onClick={() => setIsEditingJob(false)}
                                                                >
                                                                    {t("cancel")}
                                                                </Button>
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    disabled={isSaving}
                                                                    onClick={async () => {
                                                                        const signature = extractClientSignature();
                                                                        if (signature === undefined) return;

                                                                        const success = await saveJobCompletion(
                                                                            selectedTask,
                                                                            getValues(),
                                                                            noteValue,
                                                                            trigger,
                                                                            signature
                                                                        );

                                                                        if (success) {
                                                                            onClose()
                                                                            setIsEditingJob(false);
                                                                            setIsEditingEndJob(false);
                                                                        }
                                                                    }}
                                                                >
                                                                    {isSaving ? t("saving") : t("submit")}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                        </>
                                    )}
                                </Col>
                                )}
                            </Offcanvas.Body>

                            {activeSection === "discussion" && (
                                <div className="border-top bg-white px-3 py-3 flex-shrink-0">
                                        {selectedNoteImages.length > 0 && (
                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                                {selectedNoteImages.map((photo, idx) => (
                                                    <div key={idx} className="position-relative photo-thumbnail-wrapper">
                                                        <img
                                                            src={noteImageUrls[idx]}
                                                            alt={`Note photo ${idx + 1}`}
                                                            className="img-thumbnail task-photo-thumbnail pointer"
                                                            onClick={() => openGalleryPreview(noteImageUrls, idx)}
                                                        />
                                                        <span
                                                            className="photo-remove-btn position-absolute top-0 end-0 cursor-pointer text-danger lh-1 z-3"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleNoteImageRemove(idx);
                                                            }}
                                                            title={t("remove")}
                                                        >
                                                            <i className="fas fa-times-circle" />
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    <div className="chat-input-wrapper position-relative d-flex align-items-center gap-2 py-1">
                                        <button
                                            className="chat-attach-btn flex-shrink-0"
                                            type="button"
                                            title={t("attachPhoto")}
                                            onClick={() => {
                                                if (isMobileDevice) {
                                                    setShowChatAttachMenu(prev => !prev);
                                                } else {
                                                    const input = document.createElement('input');
                                                    input.type = 'file';
                                                    input.accept = 'image/*';
                                                    input.multiple = true;
                                                    input.onchange = (e) => {
                                                        if (e.target.files) {
                                                            Array.from(e.target.files).forEach(file => handleNoteImageAdd(file));
                                                        }
                                                    };
                                                    input.click();
                                                }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faImage} />
                                        </button>

                                        {showChatAttachMenu && (
                                            <>
                                                <div
                                                    className="position-fixed top-0 start-0 w-100 h-100"
                                                    style={{ zIndex: 1049 }}
                                                    onClick={() => setShowChatAttachMenu(false)}
                                                />
                                                <div className="chat-attach-menu">
                                                    {isMobileDevice && (
                                                        <button
                                                            type="button"
                                                            className="chat-attach-menu-item"
                                                            onClick={() => {
                                                                setShowChatAttachMenu(false);
                                                                const input = document.createElement('input');
                                                                input.type = 'file';
                                                                input.accept = 'image/*';
                                                                input.capture = 'environment';
                                                                input.onchange = async (e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (!file) return;
                                                                    await handleNoteImageAdd(file);
                                                                };
                                                                input.click();
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faCamera} className="me-2" />
                                                            {t("takePhoto")}
                                                        </button>
                                                    )}
                                                        <button
                                                            type="button"
                                                            className="chat-attach-menu-item"
                                                            onClick={() => {
                                                                setShowChatAttachMenu(false);
                                                                const input = document.createElement('input');
                                                                input.type = 'file';
                                                                input.accept = 'image/*';
                                                                input.multiple = true;
                                                                input.onchange = (e) => {
                                                                    if (e.target.files) {
                                                                        Array.from(e.target.files).forEach(file => handleNoteImageAdd(file));
                                                                    }
                                                                };
                                                                input.click();
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faImage} className="me-2" />
                                                            {t("uploadFromGallery")}
                                                        </button>
                                                </div>
                                            </>
                                        )}
                                        <Form.Control
                                            as="textarea"
                                            rows={1}
                                            placeholder={t("addNotePlaceholder")}
                                            value={newNoteText}
                                            onChange={(e) => setNewNoteText(e.target.value)}
                                            className="chat-input-field py-2 px-3"
                                        />
                                        <button
                                            className="chat-send-btn flex-shrink-0"
                                            disabled={isSubmittingNote || (!newNoteText.trim() && selectedNoteImages.length === 0)}                                            onClick={handleAddNote}
                                        >
                                            {isSubmittingNote ? (
                                                <Spinner animation="border" size="sm" className="chat-send-spinner" />
                                            ) : (
                                                <FontAwesomeIcon icon={faPaperPlane} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Offcanvas>
            <SignatureModal
                show={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                setCompanySignature={handleClientSignatureSave}
                userData={{}}
                mode="client"
            />
            {/* Image Preview Modal */}
            {(() => {
                const activePhotos = isEditingJob
                    ? taskPhotos.map((photo, index) => ({
                        src: taskPhotoUrls[index],
                        alt: `Task photo ${index + 1}`,
                    }))
                    : completionImages.map((img, index) => ({
                        src: `data:image/${img.ImageExtension};base64,${img.Image}`,
                        alt: `${t("taskPhotos")} ${index + 1}`,
                    }));

                const current = previewIndex !== null ? activePhotos[previewIndex] : null;

                return (
                    <Modal
                        show={previewIndex !== null}
                        onHide={() => setPreviewIndex(null)}
                        centered
                        size="lg"
                    >
                        <Modal.Header className="d-flex justify-content-between align-items-center">
                            <Modal.Title>
                                {current?.alt}
                            </Modal.Title>
                            <FalconCloseButton
                                onClick={() => setPreviewIndex(null)}
                            />
                        </Modal.Header>
                        <Modal.Body className="text-center p-2 position-relative">
                            {previewIndex > 0 && (
                                <button
                                    className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-3 shadow-sm rounded-circle photo-preview-arrow"
                                    onClick={() => setPreviewIndex(prev => prev - 1)}
                                >
                                    <i className="fas fa-chevron-left" />
                                </button>
                            )}

                            <img
                                src={current?.src}
                                alt={current?.alt}
                                className="photo-preview-img"
                            />

                            {previewIndex < activePhotos.length - 1 && (
                                <button
                                    className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-3 shadow-sm rounded-circle photo-preview-arrow"
                                    onClick={() => setPreviewIndex(prev => prev + 1)}
                                >
                                    <i className="fas fa-chevron-right" />
                                </button>
                            )}
                        </Modal.Body>
                    </Modal>
                );
            })()}
            <SchedulingIssuesModal
                show={showCombinedIssuesModal}
                issues={pendingCombinedIssues || []}
                t={t}
                onCancel={() => {
                    setShowCombinedIssuesModal(false);
                    setPendingCombinedIssues(null);
                }}
                onConfirm={async () => {
                    setShowCombinedIssuesModal(false);
                    setPendingCombinedIssues(null);
                    await proceedDateTimeSave();
                }}
            />
            <Modal
                show={!!galleryPreview}
                onHide={() => setGalleryPreview(null)}
                centered
                size="lg"
            >
                <Modal.Header className="d-flex justify-content-between align-items-center">
                    <Modal.Title>{t("sharedPhoto") || "Photo"}</Modal.Title>
                    <FalconCloseButton onClick={() => setGalleryPreview(null)} />
                </Modal.Header>
                <Modal.Body className="text-center p-2 position-relative">
                    {galleryPreview && galleryPreview.index > 0 && (
                        <button
                            className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-3 shadow-sm rounded-circle photo-preview-arrow"
                            onClick={() =>
                                setGalleryPreview(prev => ({ ...prev, index: prev.index - 1 }))
                            }
                        >
                            <i className="fas fa-chevron-left" />
                        </button>
                    )}
                    {galleryPreview && (
                        <img
                            src={galleryPreview.images[galleryPreview.index]}
                            alt={t("sharedPhoto") || "Photo"}
                            className="photo-preview-img"
                        />
                    )}
                    {galleryPreview && galleryPreview.index < galleryPreview.images.length - 1 && (
                        <button
                            className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-3 shadow-sm rounded-circle photo-preview-arrow"
                            onClick={() =>
                                setGalleryPreview(prev => ({ ...prev, index: prev.index + 1 }))
                            }
                        >
                            <i className="fas fa-chevron-right" />
                        </button>
                    )}
                </Modal.Body>
            </Modal>
            
        </>
    );
};

export default TaskDrawer;